import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Calendar, FileText, CheckCircle, XCircle, AlertCircle, ArrowUpDown, Building2, AlertTriangle, X, DollarSign, Clock, RefreshCw, Download, FilterX, AlertOctagon, History, Siren, FileSearch, ExternalLink, StickyNote, Save, PenTool, UserCheck, Bot, Sparkles, User, ChevronLeft, ChevronRight, Briefcase, Loader2, Upload, Paperclip, Wrench, Truck, Key, Layers, Edit2, Phone, Mail, Plus, Trash2, Hourglass, Shield, Edit, Eye, MoreHorizontal, List, CheckSquare, FileCheck2, ArrowRight } from 'lucide-react';
import './licitacoes-redesign.css';

import { settingsService } from '../services/settingsService';
import useCompanies from '../hooks/useCompanies';
import { Contract, RenewalRecord, CompanyProfile, AppSettings, AnalyzedFile, ContractType, ContactPerson, DocumentAttachment } from '../types';
// TODO: Implement generateAIResponse and extractContractData via API calls to backend
// import { generateAIResponse, extractContractData } from '../services/geminiService';
import { apiService } from '../services/apiService';
import { logActivity } from '../services/auditService';

import { useModal } from './ModalProvider';
import { theme } from '../config/theme';
import MoneyInput from './inputs/MoneyInput';
import { formatBRLCurrency, parseBRL, toFiniteNumber } from '../utils/money';
import { daysUntilCalendarDate, formatCalendarDate, parseCalendarDate } from '../utils/date';

const APP_SETTINGS_KEY = 'app_settings';

// Helper para gerar datas relativas para demonstração
const getFutureDate = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

  // Helper: parse YYYY-MM-DD into a Date at UTC midnight
  const parseDateOnlyToUTC = (dateStr: string | undefined | null) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    const [y, m, d] = parts;
    return new Date(Date.UTC(y, m - 1, d));
  };

  // Helper: valida e normaliza datas antes de enviar ao backend
  // Retorna undefined para strings vazias ou datas inválidas (evita enviar lixo ao servidor)
  const normalizeDateField = (dateValue: any): string | undefined => {
    if (dateValue === undefined || dateValue === null || dateValue === '') {
      return undefined;
    }
    if (typeof dateValue !== 'string') {
      return undefined;
    }
    // Aceita YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      // Valida se a data é válida
      const d = new Date(dateValue + 'T00:00:00Z');
      if (!isNaN(d.getTime())) {
        return dateValue;
      }
    }
    // Se chegou aqui, é inválido
    console.warn('[normalizeDateField] Data inválida ignorada:', dateValue);
    return undefined;
  };

    // Normaliza strings para pesquisa: remove acentos, espaços extras e normaliza barras
    const normalizeForSearch = (s?: string | null) => {
      if (!s) return '';
      try {
        return String(s)
          .normalize('NFD')
          .replace(/[\u0000-\u001f]/g, '')
          .replace(/\p{Diacritic}/gu, '')
          .replace(/\s*\/\s*/g, '/')
          .replace(/[^\w\/-\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      } catch (e) {
        // Fallback simples
        return String(s).replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').toLowerCase();
      }
    };

      // Helper: extrai actor (name/email) de um audit log retornado pelo backend
      const getActorFromLog = (log: any) => {
        if (!log) return null;
        const parsed = log.parsedDetails || (typeof log.details === 'string' ? (() => { try { return JSON.parse(log.details); } catch (e) { return null; } })() : null) || log;
        const name = parsed?.actor?.name || log.userName || log.user?.name || null;
        const email = parsed?.actor?.email || log.user?.email || null;
        const timestamp = log.timestamp || parsed?.timestamp || null;
        return { name, email, timestamp };
      };

// Contracts are now persisted in MySQL via the backend. Removed local mock data and
// automatic volume generator to ensure the app reads only from the database.

const ContractList: React.FC = () => {
  const navigate = useNavigate();

  const { showAlert, showConfirm } = useModal();

  // Empresas (hook compartilhado)
  const { companies, loading: companiesLoading, refresh: refreshCompanies, getCompanyName } = useCompanies();

  // Estado principal dos contratos — agora vindo do backend (MySQL via API)
  const [contracts, setContracts] = useState<Contract[]>([]);

  // Estado para controlar a aba ativa
  type ContractTab = 'ALL' | 'SOLAR' | 'ACTIVE_NO_BILLING' | 'CRITICAL';
  const [activeTab, setActiveTab] = useState<ContractTab>('ALL');

  // Visual-only: modo de renderização (Lista / Cards). Contratos não têm workflow Kanban.
  const [renderMode, setRenderMode] = useState<'list' | 'cards'>('list');

  // Visual-only: chips rápidos de Vigência e Tipo de contrato
  const [quickVigencia, setQuickVigencia] = useState<'ALL' | '15D' | '30D' | '60D'>('ALL');
  const [quickTipo, setQuickTipo] = useState<'ALL' | ContractType>('ALL');

  // Carregar contratos do backend ao montar o componente
  useEffect(() => {
    const loadContracts = async () => {
      try {
        const data = await apiService.getContracts();
        setContracts(data);
      } catch (error) {
        console.error('Erro ao carregar contratos do backend:', error);
      }
    };
    loadContracts();
  }, []);

  // Quando contratos são carregados, buscar logs de auditoria para cada contrato
  useEffect(() => {
    if (!contracts || contracts.length === 0) return;
    let cancelled = false;
    const fetchLogs = async () => {
      try {
        const ids = contracts.map((c) => c.id);
        const res = await apiService.getAuditLogsByResourceBatch('Contract', ids);
        // res.results is already a map id->logs in the backend; apiService returns that map
        const map: Record<string, any[]> = {};
        for (const c of contracts) {
          map[c.id] = res[c.id] || [];
        }
        if (!cancelled) setAuditMap(map);
      } catch (err) {
        // fallback to empty map per contract
        const map: Record<string, any[]> = {};
        for (const c of contracts) map[c.id] = [];
        if (!cancelled) setAuditMap(map);
      }
    };
    fetchLogs();
    return () => { cancelled = true; };
  }, [contracts]);

  // Limpar qualquer contrato legado do localStorage (não queremos dados locais)
  useEffect(() => {
    try {
      if (localStorage.getItem('licitaflow_contracts')) {
        localStorage.removeItem('licitaflow_contracts');
        console.info('Chave licitaflow_contracts removida do localStorage (migração para MySQL).');
      }
    } catch (e) {
      // ignora erros de acesso ao localStorage
    }
  }, []);

  // Preenche companyId padrão no formulário quando a lista de companies for atualizada
  useEffect(() => {
    if (companies && companies.length > 0) {
      setNewContractForm(prev => ({
        ...prev,
        companyId: prev.companyId || (companies.find((c: CompanyProfile) => c.isDefault)?.id || companies[0].id)
      }));
    }
  }, [companies]);

  // Recarregar quando for disparado o evento global
  useEffect(() => {
    const onCompaniesChanged = () => { refreshCompanies(); };
    window.addEventListener('companies:changed', onCompaniesChanged);
    return () => { window.removeEventListener('companies:changed', onCompaniesChanged); };
  }, [refreshCompanies]);

  // Estados de Filtro
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [searchTerm, setSearchTerm] = useState('');
  const [organFilter, setOrganFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<ContractType | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<'ALL' | 'PUBLIC' | 'PRIVATE'>('ALL');
  // Novo filtro de Alerta de Vencimento
  const [expirationFilter, setExpirationFilter] = useState<'ALL' | 'CRITICAL' | 'WARNING' | 'ATTENTION' | 'ALL_ALERTS'>('ALL');

  // Estados de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Estado para controlar visibilidade dos filtros avançados
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Visual-only: entra sempre em Lista, reset ao mudar de aba
  useEffect(() => { setRenderMode('list'); }, []);
  useEffect(() => { setRenderMode('list'); }, [activeTab]);

  // Chip Vigência mapeia para expirationFilter existente
  useEffect(() => {
    if (quickVigencia === 'ALL') setExpirationFilter('ALL');
    else if (quickVigencia === '15D') setExpirationFilter('CRITICAL');
    else if (quickVigencia === '30D') setExpirationFilter('WARNING');
    else if (quickVigencia === '60D') setExpirationFilter('ATTENTION');
  }, [quickVigencia]);

  // Chip Tipo mapeia para typeFilter existente
  useEffect(() => {
    setTypeFilter(quickTipo);
  }, [quickTipo]);

  // Modais e IA
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [renewContractId, setRenewContractId] = useState<string | null>(null);
  const [renewalFile, setRenewalFile] = useState<File | null>(null);
  const [renewalAdjustmentPercentage, setRenewalAdjustmentPercentage] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Estados de Edição
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Contract | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  // Novos estados para Anexos Complementares
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [attachmentsUploading, setAttachmentsUploading] = useState(false);

  // Estado para controle do menu de ações (3 pontos) por linha
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);
  const [actionsAnchorRect, setActionsAnchorRect] = useState<{ top: number; left: number } | null>(null);

  // Map de audit logs por contractId
  const [auditMap, setAuditMap] = useState<Record<string, any[]>>({});

  // Fecha o menu de ações quando clicar fora
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      if (target.closest('.actions-menu') || target.closest('.actions-button')) return;
      if (openActionsId) {
        setOpenActionsId(null);
        setActionsAnchorRect(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openActionsId]);

  // Estados de Criação
  const [isCreating, setIsCreating] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false); // Novo estado para loading da extração
  const [newContractForm, setNewContractForm] = useState<Partial<Contract>>({
      active: true,
      type: 'OTHER',
      contacts: [],
      renewalHistory: []
  });

  // Estados para Modal de Confirmação de Edição
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [existingContractToEdit, setExistingContractToEdit] = useState<any>(null);

  // Estados de IA para Resumo
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [showPromptHelp, setShowPromptHelp] = useState(false);

  // Estados para Acompanhamento de Parcelas (Contratos Solares)
  const [billingTrackingContract, setBillingTrackingContract] = useState<Contract | null>(null);
  const [isUpdatingBilling, setIsUpdatingBilling] = useState(false);

  // Prompts para extração de contratos (podemos mostrar/colar no backend ou enviar ao modelo)
  const detailedContractPrompt = `Você é um Auditor Especialista em Contratos Administrativos e Licitações. Sua tarefa é extrair dados estruturados de um documento (PDF ou texto) com precisão absoluta.\n\nREGRAS (ANTI-ALUCINAÇÃO)\n- Extraia apenas o que está escrito; não faça suposições.\n- Se não constar, responda 'NÃO CONSTA'.\n- Para cada campo inclua evidence: cláusula/página + trecho.\n- Se fim for calculado, inclua cálculo e marque como CALCULADO.\n\nCampos: title, agency, company, cnpj (apenas dígitos), object, total_value (texto), total_value_numeric, start_date (DD/MM/AAAA), end_date (DD/MM/AAAA), type (Fornecimento|Serviço|Manutenção|Locação|Outros), contract_category (PUBLIC se for contrato com órgão público/administração pública/governo, PRIVATE se for com empresa privada).\nRetorne APENAS um JSON válido com chaves *_evidence para evidências.`;

  const jsonContractPrompt = `{
  "title": "string or 'NÃO CONSTA',",
  "title_evidence": "cláusula/página + trecho",
  "agency": "string or 'NÃO CONSTA',",
  "agency_evidence": "...",
  "company": "string or 'NÃO CONSTA',",
  "company_evidence": "...",
  "cnpj": "only digits or 'NÃO CONSTA',",
  "type": "Fornecimento|Serviço|Manutenção|Locação|Outros or 'NÃO CONSTA',",
  "type_evidence": "...",
  "contract_category": "PUBLIC or PRIVATE",
  "contract_category_evidence": "menciona órgão público/prefeitura/governo ou empresa privada",
  "object": "string or 'NÃO CONSTA',",
  "total_value": "raw text or 'NÃO CONSTA',",
  "total_value_numeric": 1234.56 or null,
  "start_date": "DD/MM/AAAA or 'NÃO CONSTA',",
  "end_date": "DD/MM/AAAA or 'NÃO CONSTA',",
  "end_date_calculated": true|false
}`;

  const shortContractPrompt = `Atue como Auditor de Contratos. Extraia: title, agency, company (CNPJ), type (tipo de contrato), contract_category (PUBLIC se órgão público, PRIVATE se empresa privada), object, total_value, start_date, end_date. 'NÃO CONSTA' se ausente. Inclua evidências.`;

  // Abrir Modal
  const openDetailsModal = (contract: Contract) => {
      setSelectedContract(contract);
      // Deep copy and normalize date-only fields to yyyy-MM-dd for inputs
      const copy: any = JSON.parse(JSON.stringify(contract));
      if (copy.startDate) copy.startDate = String(copy.startDate).split('T')[0];
      if (copy.endDate) copy.endDate = String(copy.endDate).split('T')[0];
      if (copy.signatureDate) {
        // signatureDate may be full ISO; keep only date part if input type=date used
        copy.signatureDate = String(copy.signatureDate).split('T')[0];
      }
      if (copy.firstBillingDate) copy.firstBillingDate = String(copy.firstBillingDate).split('T')[0];
      setEditForm(copy); // Deep copy para editar contatos
      setIsEditing(false);
      setNoteInput(contract.notes || '');
      setAiSummary(null);

      // Acionar IA para resumir se houver descrição (apenas se não estiver editando)
      if (contract.description && contract.description.length > 20) {
          setIsSummarizing(true);

          settingsService.getAppSettings().then(settings => {
             const prompt = `
                Atue como um analista de contratos experiente.
                Analise a seguinte descrição de um contrato administrativo e gere um resumo executivo conciso.
                Descrição Original: "${contract.description}"
                Saída desejada: 1 parágrafo curto e 3 bullet points de atenção.
              `;

              // TODO: Implement generateAIResponse via API call to backend
              // generateAIResponse(prompt, { provider: settings.aiProvider, openaiKey: settings.openaiApiKey })
              //   .then(response => setAiSummary(response))
              //   .catch(err => { console.error(err); setAiSummary("Não foi possível gerar o resumo automático."); })
              //   .finally(() => setIsSummarizing(false));
              setAiSummary("Resumo automático temporariamente indisponível - implementar via API"); // Temporário
              setIsSummarizing(false);
          }).catch(() => {
             setIsSummarizing(false);
          });
      }
  };

  // Abrir modal de anexos para contrato específico (garante selecao correta)
  const openAttachmentsModal = (contract: Contract) => {
    // set selected contract and open attachments modal
    setSelectedContract(contract);
    // ensure edit form is the same normalized copy when viewing
    const copy: any = JSON.parse(JSON.stringify(contract));
    if (copy.startDate) copy.startDate = String(copy.startDate).split('T')[0];
    if (copy.endDate) copy.endDate = String(copy.endDate).split('T')[0];
    setEditForm(copy);
    setNewAttachments([]);
    setAttachmentsModalOpen(true);
  };

  // Salvar Edição Completa
  const handleSaveEdit = () => {
      if (!editForm) return;
      // Persistir alteração no backend
      (async () => {
        try {
          console.log('Saving editForm id=', editForm.id, 'payload=', JSON.stringify(editForm));
          console.log('contractFile:', contractFile);

          let fileUrl = editForm.fileUrl;

          // If there are new attachments pending, upload & persist them first so they are available after save
          if (newAttachments && newAttachments.length > 0) {
            if (!editForm.id) {
              await showAlert('Selecione um contrato válido antes de enviar anexos.');
            } else {
              setAttachmentsUploading(true);
              try {
                const targetId = editForm.id;
                for (const file of newAttachments) {
                  const safeName = file.name.replace(/\s+/g, '_');
                  const key = `contracts/${targetId}/attachments/${Date.now()}_${safeName}`;
                  const presign = await apiService.getPresignedUploadUrl(key, file.type || 'application/octet-stream');
                  await apiService.uploadFileToS3(presign.uploadUrl, file);
                  try {
                    await apiService.createContractAttachment(targetId, {
                      name: file.name,
                      url: key,
                      type: file.type,
                      size: file.size
                    });
                  } catch (createErr) {
                    console.error('Erro ao persistir anexo depois do upload (save edit):', createErr);
                  }
                }

                // Re-fetch contract so UI shows persisted attachments
                const reloaded = await apiService.getContractById(editForm.id);
                setContracts(contracts.map(c => c.id === reloaded.id ? reloaded : c));
                setSelectedContract(reloaded);
                setEditForm(reloaded);
                setNewAttachments([]);
                await showAlert('Anexos enviados e salvos com sucesso.');
              } catch (err) {
                console.error('Erro ao enviar anexos antes de salvar edição:', err);
                await showAlert('Erro ao enviar alguns anexos. As demais alterações serão salvas.');
              } finally {
                setAttachmentsUploading(false);
              }
            }
          }
          // Se um novo arquivo foi selecionado, fazer upload
          if (contractFile) {
            try {
              console.log('Iniciando upload do arquivo:', contractFile.name);
              const uploadedUrl = await apiService.uploadContractFile(contractFile);
              fileUrl = uploadedUrl;
              console.log('Arquivo enviado com sucesso:', uploadedUrl);
            } catch (uploadErr) {
              console.error('Erro ao fazer upload do arquivo:', uploadErr);
              await showAlert('Erro ao fazer upload do arquivo. As demais alterações serão salvas.');
            }
          } else {
            console.log('Nenhum arquivo novo selecionado. fileUrl atual:', fileUrl);
          }

          console.log('Atualizando contrato com fileUrl:', fileUrl);
          // Normalize empty date strings -> undefined so backend validation treats them as absent
          const payload: any = { ...editForm, fileUrl } as any;
          // Aplicar normalização de datas usando helper
          payload.startDate = normalizeDateField(payload.startDate);
          payload.endDate = normalizeDateField(payload.endDate);
          payload.signatureDate = normalizeDateField(payload.signatureDate);
          payload.firstBillingDate = normalizeDateField(payload.firstBillingDate);

          const updated = await apiService.updateContract(editForm.id, payload);
          const updatedContracts = contracts.map(c => c.id === updated.id ? updated : c);
          setContracts(updatedContracts);
          setSelectedContract(updated);
          setIsEditing(false);
          setContractFile(null);
          logActivity('CONTRATO_EDICAO', `Informações do contrato ${editForm.id} editadas e salvas no servidor.`, 'SUCCESS');
        } catch (err) {
          console.error('Erro ao salvar edição do contrato:', err);
          await showAlert('Não foi possível salvar a edição no servidor. Tente novamente.');
        }
      })();
  };

  // Função de Upload e Extração IA
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsExtracting(true);
      try {
          // Use backend extraction endpoint
          try {
            const extractedData = await apiService.extractContractFromPDF(file);

            const parseDDMMYYYY = (s?: string | null) => {
              if (!s || s === 'NÃO CONSTA') return undefined;
              const parts = s.split('/');
              if (parts.length !== 3) return undefined;
              return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            };

            const parseCurrencyToNumber = (v?: any) => {
              if (!v || v === 'NÃO CONSTA') return undefined;
              if (typeof v === 'number') return v;
              try {
                // Remove non digits, keep comma and dot; handle BRL like R$ 1.234,56
                const cleaned = String(v).replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(/,/g, '.');
                const n = parseFloat(cleaned);
                return isNaN(n) ? undefined : n;
              } catch (e) { return undefined; }
            };

            const mapCategory = (cat?: string) => {
              if (!cat) return undefined;
              const c = cat.toLowerCase();
              if (c.includes('ata de registro') || c.includes('registro de preco') || c.includes('registro de preço')) return 'PRICE_REGISTRATION';
              if (c.includes('fornecimento')) return 'SUPPLY';
              if (c.includes('servi')) return 'SERVICE';
              if (c.includes('manuten')) return 'MAINTENANCE';
              if (c.includes('loca')) return 'RENTAL';
              if (c === 'não consta' || c === "nÃo consta") return undefined;
              return 'OTHER';
            };

            const mapContractCategory = (cat?: string) => {
              if (!cat) return 'PUBLIC'; // default
              const c = cat.toUpperCase();
              if (c === 'PRIVATE' || c === 'PRIVADO') return 'PRIVATE';
              return 'PUBLIC';
            };

            if (extractedData) {
              setNewContractForm(prev => ({
                ...prev,
                title: extractedData.title && extractedData.title !== 'NÃO CONSTA' ? extractedData.title : prev.title,
                organ: extractedData.agency && extractedData.agency !== 'NÃO CONSTA' ? extractedData.agency : prev.organ,
                startDate: parseDDMMYYYY(extractedData.start_date) || prev.startDate,
                endDate: parseDDMMYYYY(extractedData.end_date) || prev.endDate,
                value: parseCurrencyToNumber(extractedData.total_value) ?? prev.value,
                type: (mapCategory(extractedData.type || extractedData.category) as any) || prev.type,
                category: mapContractCategory(extractedData.contract_category) || prev.category,
                description: extractedData.object && extractedData.object !== 'NÃO CONSTA' ? extractedData.object : prev.description,
                // Preencher número do contrato quando disponível pela extração IA
                contractNumber: extractedData.contractNumber && extractedData.contractNumber !== 'NÃO CONSTA' ? extractedData.contractNumber : prev.contractNumber
              }));

              // Salvar o arquivo para upload posterior
              setContractFile(file);
              console.log('Arquivo PDF salvo para upload:', file.name);

              await showAlert('Dados extraídos com sucesso! Verifique os campos preenchidos.');
              console.log('Evidências extração contrato:', extractedData);
              logActivity('CONTRATO_EXTRAIDO_IA', `Contrato extraído via IA: ${extractedData.title || extractedData.title_evidence || ''}`, 'SUCCESS');
            } else {
              await showAlert('Não foi possível extrair dados do contrato. Tente novamente ou preencha manualmente.');
            }
          } catch (err) {
            console.error('Erro ao chamar a extração de contrato:', err);
            await showAlert('Erro ao extrair dados do contrato.');
          }
          setIsExtracting(false);
    } catch (error) {
          console.error("Erro no upload:", error);
          setIsExtracting(false);
      await showAlert("Erro ao processar arquivo.");
      }
  };

  // Criar Novo Contrato
  const handleCreateContract = async () => {
      if (!newContractForm.title || !newContractForm.organ || !newContractForm.startDate || !newContractForm.endDate || !newContractForm.companyId) {
      await showAlert("Preencha os campos obrigatórios (Título, Órgão, Empresa, Início, Fim).");
          return;
      }

      try {
          let fileUrl = newContractForm.fileUrl;

          // Se um arquivo foi selecionado, fazer upload
          if (contractFile) {
            try {
              console.log('Fazendo upload do arquivo antes de criar contrato:', contractFile.name);
              const uploadedUrl = await apiService.uploadContractFile(contractFile);
              fileUrl = uploadedUrl;
              console.log('Arquivo enviado com sucesso:', uploadedUrl);
            } catch (uploadErr) {
              console.error('Erro ao fazer upload do arquivo:', uploadErr);
              await showAlert('Erro ao fazer upload do arquivo. O contrato será criado sem anexo.');
            }
          }

          // Se houver anexos selecionados para o novo contrato, subir para S3 em pasta temporária
          let attachmentsPayload: DocumentAttachment[] | undefined = undefined;
          if (newAttachments && newAttachments.length > 0) {
            try {
              const uploadedAttachments: DocumentAttachment[] = [];
              for (const file of newAttachments) {
                const safeName = file.name.replace(/\s+/g, '_');
                const companyPart = (newContractForm.companyId || 'unknown');
                const key = `contracts/temp/${companyPart}/${Date.now()}_${safeName}`;
                const presign = await apiService.getPresignedUploadUrl(key, file.type || 'application/octet-stream');
                await apiService.uploadFileToS3(presign.uploadUrl, file);

                uploadedAttachments.push({
                  id: `${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
                  name: file.name,
                  url: key,
                  type: file.type,
                  size: file.size,
                  uploadedAt: new Date().toISOString()
                });
              }
              attachmentsPayload = uploadedAttachments;
            } catch (err) {
              console.error('Erro ao enviar anexos do novo contrato:', err);
              await showAlert('Erro ao enviar alguns anexos. O contrato será criado sem esses anexos.');
            }
          }

          // Normalize empty date strings -> undefined
          const createPayload: any = { ...newContractForm, fileUrl } as any;
          if (attachmentsPayload) createPayload.attachments = attachmentsPayload;
          // Aplicar normalização de datas usando helper
          createPayload.startDate = normalizeDateField(createPayload.startDate);
          createPayload.endDate = normalizeDateField(createPayload.endDate);
          createPayload.signatureDate = normalizeDateField(createPayload.signatureDate);
          createPayload.firstBillingDate = normalizeDateField(createPayload.firstBillingDate);

          const created = await apiService.createContract(createPayload as any);
          setContracts([...contracts, created]);
          setIsCreating(false);
          setNewContractForm({ active: true, type: 'OTHER', contacts: [], renewalHistory: [] });
          setNewAttachments([]);
          setContractFile(null);
          logActivity('CONTRATO_CRIACAO', `Novo contrato ${created.title} criado.`, 'SUCCESS');
    } catch (err: any) {
      console.error('Erro ao criar contrato:', err);
      console.log('DEBUG - Estrutura do erro:', {
        hasResponse: !!err?.response,
        status: err?.response?.status,
        hasData: !!err?.response?.data,
        hasExistingContract: !!err?.response?.data?.existingContract,
        fullError: err
      });

      // Verificar se é erro 409 (contrato duplicado) com opção de editar
      if (err?.response?.status === 409 && err?.response?.data?.existingContract) {
        const responseData = err.response.data;
        console.log('✅ Detectado erro 409 - Abrindo modal de edição');
        setExistingContractToEdit(responseData.existingContract);
        setShowEditConfirmModal(true);
        return;
      }

      const errorMessage = err?.response?.data?.error || err?.message || 'Erro ao criar contrato. Verifique os dados e tente novamente.';
      await showAlert(errorMessage);
    }
  };

  // Abrir o contrato existente para edição
  const handleEditExistingContract = async () => {
    if (!existingContractToEdit) return;

    try {
      // Buscar o contrato completo do backend
      const fullContract = await apiService.getContractById(existingContractToEdit.id);
      setSelectedContract(fullContract);
      setEditForm(fullContract);
      setIsEditing(true);
      setShowEditConfirmModal(false);
      setIsCreating(false);
      setExistingContractToEdit(null);

      // Limpar o formulário de criação
      setNewContractForm({ active: true, type: 'OTHER', contacts: [], renewalHistory: [] });
      setContractFile(null);
    } catch (err) {
      console.error('Erro ao buscar contrato para edição:', err);
      await showAlert('Erro ao abrir o contrato para edição.');
    }
  };

  // Cancelar e voltar para o formulário de criação
  const handleCancelEditExisting = () => {
    setShowEditConfirmModal(false);
    setExistingContractToEdit(null);
  };

  // Excluir Contrato
  const handleDeleteContract = async () => {
    if (!selectedContract) return;
    const ok = await showConfirm(`Tem certeza que deseja excluir o contrato "${selectedContract.title}"? Esta ação não pode ser desfeita.`);
    if (!ok) return;
    try {
      await apiService.deleteContract(selectedContract.id);
      setContracts(contracts.filter(c => c.id !== selectedContract.id));
      setSelectedContract(null);
      setIsEditing(false);
      logActivity('CONTRATO_EXCLUSAO', `Contrato ${selectedContract.title} excluído.`, 'WARNING');
    } catch (err) {
      console.error('Erro ao excluir contrato:', err);
      await showAlert('Erro ao excluir contrato.');
    }
  };

  // Funções de Gerenciamento de Contatos no EditForm
  const handleAddContact = () => {
      if (!editForm) return;
      const newContact: ContactPerson = {
          id: Date.now().toString(),
          name: '',
          role: '',
          email: '',
          phone: ''
      };
      setEditForm({
          ...editForm,
          contacts: [...(editForm.contacts || []), newContact]
      });
  };

  const handleRemoveContact = (contactId: string) => {
      if (!editForm) return;
      setEditForm({
          ...editForm,
          contacts: editForm.contacts.filter(c => c.id !== contactId)
      });
  };

  const handleContactChange = (contactId: string, field: keyof ContactPerson, value: string) => {
      if (!editForm) return;
      const updatedContacts = editForm.contacts.map(c =>
          c.id === contactId ? { ...c, [field]: value } : c
      );
      setEditForm({ ...editForm, contacts: updatedContacts });
  };

  // Função para atualizar parcelas faturadas (Contratos Solares)
  const handleUpdateBilling = async (contractId: string, newBilledInstallments: number) => {
    setIsUpdatingBilling(true);
    try {
      // Atualiza no backend
      await apiService.updateContract(contractId, {
        billedInstallments: newBilledInstallments
      });

      // Atualiza localmente
      setContracts(contracts.map(c =>
        c.id === contractId
          ? { ...c, billedInstallments: newBilledInstallments }
          : c
      ));

      // Atualiza o contrato do modal se estiver aberto
      if (billingTrackingContract && billingTrackingContract.id === contractId) {
        setBillingTrackingContract({
          ...billingTrackingContract,
          billedInstallments: newBilledInstallments
        });
      }

      await showAlert('✅ Faturamento atualizado com sucesso!');
      logActivity('CONTRATO_FATURAMENTO', `Parcelas atualizadas: ${newBilledInstallments}`, 'INFO');
    } catch (err) {
      console.error('Erro ao atualizar faturamento:', err);
      await showAlert('❌ Erro ao atualizar faturamento.');
    } finally {
      setIsUpdatingBilling(false);
    }
  };

  // Resetar paginação quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, organFilter, descriptionFilter, statusFilter, startDateFilter, endDateFilter, companyFilter, typeFilter, categoryFilter, expirationFilter]);

  // getCompanyName provided by hook

  // Helper para configuração do Tipo de Contrato
  const getTypeConfig = (type: ContractType) => {
    // Use centralized badge classes from theme where possible
    switch (type) {
      case 'SUPPLY': return { label: 'Fornecimento', icon: <Truck className="w-3 h-3" />, color: theme.classes.badge.info };
      case 'SERVICE': return { label: 'Serviço', icon: <Briefcase className="w-3 h-3" />, color: theme.classes.badge.neutral };
  case 'MAINTENANCE': return { label: 'Manutenção', icon: <Wrench className="w-3 h-3" />, color: theme.classes.badge.primary };
      case 'RENTAL': return { label: 'Locação', icon: <Key className="w-3 h-3" />, color: theme.classes.badge.info };
      case 'RENTAL_MAINTENANCE': return { label: 'Locação + Manutenção', icon: <span className="flex items-center gap-1"><Key className="w-3 h-3" /><Wrench className="w-3 h-3" /></span>, color: theme.classes.badge.warning };
      case 'PRICE_REGISTRATION': return { label: 'Ata de Registro de Preço', icon: <Layers className="w-3 h-3" />, color: theme.classes.badge.primary };
      default: return { label: 'Outros', icon: <FileText className="w-3 h-3" />, color: theme.classes.badge.neutral };
    }
  };

  // Helper para dias restantes
  const getDaysRemaining = (dateString: string) => {
    return daysUntilCalendarDate(dateString) ?? Number.POSITIVE_INFINITY;
  };

  // Lógica de Filtro e Ordenação
  const filteredContracts = contracts
    .filter(contract => {
      // Filtro por aba ativa (prioridade máxima)
      if (activeTab === 'SOLAR' && contract.contractType !== 'SOLAR') return false;
      if (activeTab === 'ACTIVE_NO_BILLING' && !(contract.active && (contract.billedInstallments || 0) === 0)) return false;
      if (activeTab === 'CRITICAL') {
        const days = getDaysRemaining(contract.endDate);
        if (!(contract.active && days >= 0 && days <= 15)) return false;
      }

      // Filtros regulares
      const lowerSearch = normalizeForSearch(searchTerm);
      const normalizedTitle = normalizeForSearch(contract.title);
      const normalizedNumber = normalizeForSearch(contract.contractNumber || '');
      const normalizedId = normalizeForSearch(contract.id);
      const matchesSearch =
        normalizedTitle.includes(lowerSearch) ||
        normalizedNumber.includes(lowerSearch) ||
        normalizedId.includes(lowerSearch);
      const matchesOrgan = normalizeForSearch(contract.organ).includes(normalizeForSearch(organFilter));
      const matchesDescription = normalizeForSearch(contract.description).includes(normalizeForSearch(descriptionFilter));
      const matchesStatus = statusFilter === 'ALL' ? true : statusFilter === 'ACTIVE' ? contract.active : !contract.active;
      const matchesCompany = companyFilter === 'ALL' || contract.companyId === companyFilter;

      let matchesDate = true;
      if (startDateFilter || endDateFilter) {
        const contractStart = new Date(contract.startDate);
        if (startDateFilter) matchesDate = matchesDate && contractStart >= new Date(startDateFilter);
        if (endDateFilter) matchesDate = matchesDate && contractStart <= new Date(endDateFilter);
      }

      const matchesType = typeFilter === 'ALL' || contract.type === typeFilter;
      const matchesCategory = categoryFilter === 'ALL' || contract.category === categoryFilter;

      // Filtro de Alerta de Vencimento
      let matchesExpiration = true;
      if (expirationFilter !== 'ALL') {
          const days = getDaysRemaining(contract.endDate);
          // Alerta só faz sentido para contratos ativos
          if (contract.active) {
              if (expirationFilter === 'CRITICAL') matchesExpiration = days >= 0 && days <= 15;
              else if (expirationFilter === 'WARNING') matchesExpiration = days > 15 && days <= 30;
              else if (expirationFilter === 'ATTENTION') matchesExpiration = days > 30 && days <= 60;
              else if (expirationFilter === 'ALL_ALERTS') matchesExpiration = days >= 0 && days <= 60;
          } else {
              matchesExpiration = false;
          }
      }

      return matchesSearch && matchesOrgan && matchesDescription && matchesStatus && matchesDate && matchesCompany && matchesType && matchesCategory && matchesExpiration;
    })
    .sort((a, b) => {
      const dateA = new Date(a.endDate).getTime();
      const dateB = new Date(b.endDate).getTime();
      return sortOrder === 'ASC' ? dateA - dateB : dateB - dateA;
    });

  // Lógica de Paginação
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentContracts = filteredContracts.slice(startIndex, endIndex);

  // Contagem de filtros ativos
  const activeFiltersCount = [searchTerm, organFilter, descriptionFilter, startDateFilter, endDateFilter].filter(Boolean).length
                            + (statusFilter !== 'ALL' ? 1 : 0)
                            + (companyFilter !== 'ALL' ? 1 : 0)
                            + (typeFilter !== 'ALL' ? 1 : 0)
                            + (categoryFilter !== 'ALL' ? 1 : 0)
                            + (expirationFilter !== 'ALL' ? 1 : 0);

  // Centralized input classes for theme consistency
  const inputClass = `${theme.classes.input.base} text-sm`;
  const inputIconClass = `${inputClass} pl-10`;

  const clearFilters = () => {
    setSearchTerm('');
    setOrganFilter('');
    setDescriptionFilter('');
    setStatusFilter('ALL');
    setStartDateFilter('');
    setEndDateFilter('');
    setCompanyFilter('ALL');
    setTypeFilter('ALL');
    setCategoryFilter('ALL');
    setExpirationFilter('ALL');
    setSortOrder('ASC');
  };

  // Lógica de Exportação CSV
  const handleExportCSV = async () => {
    const dataToExport = filteredContracts;

    if (dataToExport.length === 0) {
      await showAlert("Não há contratos para exportar com os filtros atuais.");
      return;
    }

    const headers = ['Número do Contrato', 'Tipo', 'Empresa', 'Título', 'Órgão', 'Responsável Principal', 'Contatos', 'Valor Global', 'Valor Mensal Vigente', 'Início Vigência', 'Fim Vigência', 'Status', 'Descrição', 'Observações'];

    const rows = dataToExport.map(contract => [
      contract.contractNumber || contract.id,
      getTypeConfig(contract.type).label,
      `"${(getCompanyName(contract.companyId) || 'N/A').replace(/"/g, '""')}"`,
      `"${contract.title.replace(/"/g, '""')}"`,
      `"${contract.organ.replace(/"/g, '""')}"`,
      `"${(contract.contacts?.[0]?.name || 'N/A').replace(/"/g, '""')}"`,
      `"${(contract.contacts?.map(c => `${c.name} (${c.email})`).join('; ') || '').replace(/"/g, '""')}"`,
      `"${contract.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}"`,
      `"${contract.monthlyValue == null ? '' : formatBRLCurrency(contract.monthlyValue)}"`,
      formatCalendarDate(contract.startDate),
      formatCalendarDate(contract.endDate),
      contract.active ? 'Ativo' : 'Inativo',
      `"${contract.description.replace(/"/g, '""')}"`,
      `"${(contract.notes || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = activeFiltersCount > 0
      ? `contratos_filtrados_${dateStr}.csv`
      : `contratos_todos_${dateStr}.csv`;

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Lógica de Salvar Observação e Empresa (Modo Visualização)
  const handleSaveDetails = (newCompanyId?: string, newType?: ContractType, newCategory?: string) => {
    if (!selectedContract) return;
    setIsSavingNote(true);
    (async () => {
      try {
        const payload: any = { notes: noteInput };
        if (newCompanyId !== undefined) payload.companyId = newCompanyId;
        if (newType !== undefined) payload.type = newType;
        if (newCategory !== undefined) payload.category = newCategory;

        const updated = await apiService.updateContract(selectedContract.id, payload);
        const updatedContracts = contracts.map(c => c.id === updated.id ? updated : c);
        setContracts(updatedContracts);
        setSelectedContract(updated);
      } catch (err) {
        console.error('Erro ao salvar detalhes do contrato:', err);
        await showAlert('Não foi possível salvar os detalhes no servidor.');
      } finally {
        setIsSavingNote(false);
      }
    })();
  };

  const handleConfirmRenewal = () => {
    if (!renewContractId) return;
    const contract = contracts.find(c => c.id === renewContractId);
    if (!contract) return;

    const adjustmentPercentage = Number(renewalAdjustmentPercentage.replace(',', '.'));
    if (!renewalAdjustmentPercentage.trim() || !Number.isFinite(adjustmentPercentage) || adjustmentPercentage <= -100 || adjustmentPercentage > 1000) {
      void showAlert('Informe um percentual de reajuste maior que -100% e de até 1.000%.');
      return;
    }
    if (contract.monthlyValue == null) {
      void showAlert('Informe o valor mensal vigente no cadastro do contrato antes de renovar.');
      return;
    }

    const applyRenewal = (attachedFile: AnalyzedFile | null) => {
        (async () => {
          try {
            const currentEndDate = new Date(contract.endDate);
            const newEndDate = new Date(currentEndDate);
            newEndDate.setFullYear(newEndDate.getFullYear() + 1);

            const newEndDateStr = newEndDate.toISOString().split('T')[0];
            const renewalDateStr = new Date().toISOString().split('T')[0];

            // Call API to create renewal
            const updated = await apiService.createRenewal(renewContractId, {
              renewalDate: renewalDateStr,
              newEndDate: newEndDateStr,
              adjustmentPercentage,
            });
            const updatedContracts = contracts.map(c => c.id === updated.id ? updated : c);
            setContracts(updatedContracts);
            if (selectedContract?.id === updated.id) setSelectedContract(updated);
          } catch (err) {
            console.error('Erro ao aplicar renovação:', err);
            await showAlert('Não foi possível aplicar a renovação no servidor.');
          } finally {
            setRenewContractId(null);
            setRenewalFile(null);
            setRenewalAdjustmentPercentage('');
          }
        })();
    };

    if (renewalFile) {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result as string;
            const base64Data = base64String.split(',')[1];
            const analyzedFile: AnalyzedFile = {
                name: renewalFile.name,
                type: renewalFile.type,
                size: renewalFile.size,
                base64: base64Data
            };
            applyRenewal(analyzedFile);
        };
        reader.readAsDataURL(renewalFile);
    } else {
        applyRenewal(null);
    }
  };

  const handleDownloadFile = async (contract: Contract) => {
    try {
        // Prioridade 1: Se tem file.url (arquivo no S3 via AnalyzedFile)
        if (contract.file?.url) {
            const { downloadUrl } = await apiService.getPresignedDownloadUrl(contract.file.url);
            window.open(downloadUrl, '_blank');
            return;
        }

        // Prioridade 2: Se tem fileUrl diretamente do backend
        if (contract.fileUrl) {
            const { downloadUrl } = await apiService.getPresignedDownloadUrl(contract.fileUrl);
            window.open(downloadUrl, '_blank');
            return;
        }

        // Prioridade 3: Se tem base64 local
        if (contract.file?.base64) {
            const link = document.createElement('a');
            link.href = `data:${contract.file.type};base64,${contract.file.base64}`;
            link.download = contract.file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        await showAlert('Nenhum arquivo anexado a este contrato.');
    } catch (error) {
        console.error('Erro ao baixar arquivo:', error);
        await showAlert('Erro ao baixar o arquivo do contrato.');
    }
  };

  const handleDownloadRenewalFile = async (file: AnalyzedFile) => {
    try {
        // Se tem url (arquivo no S3), buscar URL assinada
        if (file.url) {
            const { downloadUrl } = await apiService.getPresignedDownloadUrl(file.url);
            window.open(downloadUrl, '_blank');
            return;
        }

        // Se tem base64 local
        if (file.base64) {
            const link = document.createElement('a');
            link.href = `data:${file.type};base64,${file.base64}`;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        await showAlert('Arquivo inválido.');
    } catch (error) {
        console.error('Erro ao baixar arquivo:', error);
        await showAlert('Erro ao baixar o arquivo.');
    }
  };

  // --- Anexos Complementares (múltiplos) ---
  const handleAddAttachments = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    setNewAttachments(prev => [...prev, ...files]);
  };

  const handleRemoveNewAttachment = (index: number) => {
    setNewAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadAttachments = async () => {
    // target can be the selectedContract (view mode) or the editForm (when editing)
    const targetId = (selectedContract && selectedContract.id) || (editForm && editForm.id);
    if (!targetId) return await showAlert('Selecione um contrato para anexar os arquivos.');
    if (newAttachments.length === 0) {
      await showAlert('Nenhum arquivo selecionado para enviar.');
      return;
    }

    setAttachmentsUploading(true);
    try {
      const uploadedAttachments: DocumentAttachment[] = [];
      for (const file of newAttachments) {
        const safeName = file.name.replace(/\s+/g, '_');
        const key = `contracts/${targetId}/attachments/${Date.now()}_${safeName}`;
        const presign = await apiService.getPresignedUploadUrl(key, file.type || 'application/octet-stream');
        await apiService.uploadFileToS3(presign.uploadUrl, file);

        uploadedAttachments.push({
          id: `${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
          name: file.name,
          url: key,
          type: file.type,
          size: file.size,
          uploadedAt: new Date().toISOString()
        });
      }

      // Merge with existing attachments on whichever object we have
      const existing = (selectedContract && selectedContract.attachments) || (editForm && editForm.attachments) || [];
      // Persist each uploaded attachment individually via the new endpoint
      const createdAttachments: DocumentAttachment[] = [];
      for (const att of uploadedAttachments) {
        try {
          const created = await apiService.createContractAttachment(targetId, {
            name: att.name,
            url: att.url,
            type: att.type,
            size: att.size,
          });
          createdAttachments.push(created as DocumentAttachment);
        } catch (createErr) {
          console.error('Erro ao criar anexo no servidor para', att.name, createErr);
        }
      }

      // Re-fetch the contract to get authoritative state
      const reloaded = await apiService.getContractById(targetId);
      setContracts(contracts.map(c => c.id === reloaded.id ? reloaded : c));
      setSelectedContract(reloaded);
      setEditForm(reloaded);
      setNewAttachments([]);

      const persistedCount = (reloaded.attachments || []).length;
      if (persistedCount < (existing || []).length + uploadedAttachments.length) {
        console.warn(`[WARN] Persistidos ${persistedCount}, esperados ${(existing || []).length + uploadedAttachments.length}`);
        await showAlert(`Aviso: alguns anexos podem não ter sido salvos. Persistidos: ${persistedCount}.`);
      } else {
        await showAlert('Anexos enviados com sucesso.');
      }
      logActivity('CONTRATO_ANEXOS_UPLOAD', `Enviados ${uploadedAttachments.length} anexos para o contrato ${targetId}. Persistidos: ${persistedCount}`, 'SUCCESS');
    } catch (err) {
      console.error('Erro ao enviar anexos:', err);
      await showAlert('Erro ao enviar anexos. Tente novamente.');
    } finally {
      setAttachmentsUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment: DocumentAttachment) => {
    try {
      if (attachment.url) {
        const { downloadUrl } = await apiService.getPresignedDownloadUrl(attachment.url);
        window.open(downloadUrl, '_blank');
        return;
      }
      await showAlert('Arquivo inválido.');
    } catch (err) {
      console.error('Erro ao baixar anexo:', err);
      await showAlert('Erro ao baixar anexo.');
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    const targetId = (selectedContract && selectedContract.id) || (editForm && editForm.id);
    if (!targetId) return;
    const ok = await showConfirm('Deseja remover este anexo complementar? Esta ação não exclui o arquivo no S3.');
    if (!ok) return;
    try {
      // Call new endpoint to delete attachment metadata
      await apiService.deleteContractAttachment(targetId, attachmentId);
      // Re-fetch contract to get updated attachments
      const reloaded = await apiService.getContractById(targetId);
      setContracts(contracts.map(c => c.id === reloaded.id ? reloaded : c));
      setSelectedContract(reloaded);
      setEditForm(reloaded);
      await showAlert('Anexo removido.');
      logActivity('CONTRATO_ANEXO_REMOVIDO', `Anexo ${attachmentId} removido do contrato ${targetId}`, 'INFO');
    } catch (err) {
      console.error('Erro ao remover anexo:', err);
      await showAlert('Erro ao remover anexo.');
    }
  };

  const contractToRenew = contracts.find(c => c.id === renewContractId);
  const renewalPercentagePreview = Number(renewalAdjustmentPercentage.replace(',', '.'));
  const renewalMonthlyValuePreview = contractToRenew?.monthlyValue != null && Number.isFinite(renewalPercentagePreview)
    ? Math.round((contractToRenew.monthlyValue * (1 + renewalPercentagePreview / 100) + Number.EPSILON) * 100) / 100
    : null;

  return (
    <>
  <div className={`${theme.classes.card.base} overflow-hidden flex flex-col min-h-[calc(100vh-8rem)] max-w-full`}>

        {/* Navegação por Abas — unificada em brand-500 (estilo terracota) */}
        <div className="border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-x-auto">
          <div className="flex scrollbar-thin px-2">
            {([
              { key: 'ALL',                label: 'Todos os Contratos',       shortLabel: 'Todos',     Icon: FileCheck2,    count: contracts.length },
              { key: 'SOLAR',              label: 'Contratos Solares',        shortLabel: 'Solares',   Icon: Sparkles,       count: contracts.filter(c => c.contractType === 'SOLAR').length },
              { key: 'ACTIVE_NO_BILLING',  label: 'Vigentes sem Faturamento', shortLabel: 'Sem Fatur.', Icon: AlertTriangle, count: contracts.filter(c => c.active && (c.billedInstallments || 0) === 0).length },
              { key: 'CRITICAL',           label: 'Vencimento Crítico',       shortLabel: 'Críticos',  Icon: AlertOctagon,   count: contracts.filter(c => c.active && getDaysRemaining(c.endDate) >= 0 && getDaysRemaining(c.endDate) <= 15).length },
            ] as const).map(tab => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                  className={[
                    'flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-[13px] font-medium whitespace-nowrap transition-colors relative',
                    active
                      ? 'text-brand-700 dark:text-brand-300 after:content-[""] after:absolute after:left-3 after:right-3 after:bottom-0 after:h-[2px] after:bg-brand-500 after:rounded-full'
                      : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark',
                  ].join(' ')}
                >
                  <tab.Icon className={`w-3.5 h-3.5 ${active ? 'text-brand-500' : ''}`} />
                  <span className="hidden lg:inline">{tab.label}</span>
                  <span className="lg:hidden">{tab.shortLabel}</span>
                  <span
                    className={[
                      'text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full tabular',
                      active
                        ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300'
                        : 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark',
                    ].join(' ')}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Mensagem de contexto da aba ativa */}
          {activeTab !== 'ALL' && (
            <div className={`px-6 py-3 text-sm flex items-center gap-2 ${
              activeTab === 'SOLAR' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-b border-brand-200 dark:border-[rgba(203,106,30,0.30)]' :
              activeTab === 'ACTIVE_NO_BILLING' ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-b border-yellow-100' :
              'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-b border-red-100 dark:border-red-800/50'
            }`}>
              {activeTab === 'SOLAR' && (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                  </svg>
                  <span>Exibindo apenas contratos do tipo <strong>Solar</strong> (Locação + Comodato)</span>
                </>
              )}

              {/* Seção de Anexos Complementares (em edição) */}
              {isEditing && (
                <div className="mt-6 bg-surface-alt dark:bg-surface-dark-alt rounded-xl border border-line dark:border-line-dark p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2"><Paperclip className="w-4 h-4" /> Anexos Complementares</span>
                    <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Adicione, visualize ou remova arquivos</span>
                  </div>

                  <div className="bg-surface dark:bg-surface-dark rounded-lg border border-line dark:border-line-dark p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <label className="flex-1 cursor-pointer bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong border-dashed rounded-lg p-2 text-center hover:bg-blue-50 transition-colors">
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple onChange={handleAddAttachments} />
                        <div className="flex items-center justify-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                          <Upload className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /> Selecionar arquivos
                        </div>
                      </label>
                      <button onClick={handleUploadAttachments} disabled={attachmentsUploading || newAttachments.length === 0} className={`px-3 py-2 rounded text-sm ${attachmentsUploading || newAttachments.length === 0 ? 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark' : 'bg-brand-500 text-white hover:bg-brand-600'}`}>
                        {attachmentsUploading ? 'Enviando...' : 'Enviar Anexos'}
                      </button>
                    </div>

                    {/* Lista de anexos pendentes */}
                    {newAttachments.length > 0 && (
                      <div className="mb-3 text-sm">
                        <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">Arquivos para enviar:</div>
                        <div className="space-y-1">
                          {newAttachments.map((f, i) => (
                            <div key={i} className="flex items-center justify-between gap-3">
                              <div className="truncate text-sm">{f.name} <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">({(f.size/1024).toFixed(2)} KB)</span></div>
                              <button onClick={() => handleRemoveNewAttachment(i)} className="text-red-500 dark:text-red-400 text-xs">Remover</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Lista de anexos já salvos */}
                    <div className="mt-2">
                      <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">Anexos já salvos:</div>
                      {((isEditing ? (editForm?.attachments || []) : (selectedContract?.attachments || []))?.length || 0) > 0 ? (
                        <div className="space-y-2">
                          {(isEditing ? (editForm?.attachments || []) : (selectedContract?.attachments || [])).map(att => (
                            <div key={att.id} className="flex items-center justify-between gap-3 text-sm">
                              <div className="truncate">{att.name} <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{att.size ? `(${(att.size/1024).toFixed(2)} KB)` : ''}</span></div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleDownloadAttachment(att)} className="text-blue-600 dark:text-blue-300 text-xs">Visualizar</button>
                                <button onClick={() => handleDeleteAttachment(att.id)} className="text-red-500 dark:text-red-400 text-xs">Remover</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Nenhum anexo salvo.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'ACTIVE_NO_BILLING' && (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  <span>Contratos <strong>vigentes</strong> mas que ainda <strong>não possuem parcelas faturadas</strong></span>
                </>
              )}
              {activeTab === 'CRITICAL' && (
                <>
                  <AlertOctagon className="w-4 h-4 animate-pulse" />
                  <span>Contratos ativos que <strong>vencem em até 15 dias</strong> - Ação urgente necessária!</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Seção de Filtros */}
        <div className="p-4 md:p-6 border-b border-line dark:border-line-dark flex flex-col gap-4 flex-shrink-0">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <FileCheck2 className="w-5 h-5 text-brand-700 dark:text-brand-300" />
                Gestão de Contratos
              </h2>
              <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Gerencie vigências, renovações e acompanhe detalhes contratuais</p>
              {/* Exibe resumo rápido das empresas cadastradas ou link para criação */}
              {companies.length === 0 ? (
                <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-2 flex items-center gap-2">
                  Nenhuma empresa cadastrada.{' '}
                  <button onClick={() => navigate('/settings')} className="text-blue-600 dark:text-blue-300 underline text-sm">Cadastrar empresa</button>
                </div>
              ) : (
                <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-2">Empresas cadastradas: <span className="font-medium text-ink-text dark:text-ink-text-dark">{companies.length}</span></div>
              )}
            </div>
              <div className="flex flex-wrap gap-2">
              <button onClick={() => setIsCreating(true)} className={`${theme.classes.button.primary} flex items-center gap-2 px-3 md:px-4 py-2 text-sm`}>
                  <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Novo</span> Contrato
              </button>
              {activeFiltersCount > 0 && (
                <button onClick={clearFilters} className={`${theme.classes.button.outline} flex items-center gap-2 px-2 md:px-3 py-2 text-sm`}>
                  <FilterX className="w-4 h-4" /> <span className="hidden sm:inline">Limpar</span>
                </button>
              )}
              <button onClick={handleExportCSV} className={`${theme.classes.button.outline} flex items-center gap-2 px-2 md:px-4 py-2 text-sm whitespace-nowrap`}>
                <Download className="w-4 h-4" /> <span className="hidden sm:inline">{activeFiltersCount > 0 ? 'Exportar (Filtrado)' : 'Exportar CSV'}</span><span className="sm:hidden">CSV</span>
              </button>
            </div>
          </div>
        {/* KPI Strip — 4 tiles (computados dos contracts já carregados) */}
        {(() => {
          const activos = contracts.filter(c => c.active);
          const vencendo15 = contracts.filter(c => c.active && getDaysRemaining(c.endDate) >= 0 && getDaysRemaining(c.endDate) <= 15).length;
          const valorTotal = activos.reduce((a, c) => a + toFiniteNumber(c.value), 0);
          const solarTotal = contracts.filter(c => c.contractType === 'SOLAR').length;
          const solarFaturando = contracts.filter(c => c.contractType === 'SOLAR' && (c.billedInstallments || 0) > 0).length;
          const fmtCompactBRL = (v: number) => {
            if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
            if (v >= 1_000) return `R$ ${Math.round(v / 1_000)}K`;
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
          };
          return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 flex items-center justify-center flex-shrink-0">
                  <FileCheck2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Contratos ativos</div>
                  <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{activos.length}<span className="text-[14px] font-normal text-ink-subtitle dark:text-ink-subtitle-dark">/{contracts.length}</span></div>
                </div>
              </div>
              <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-danger-soft dark:bg-danger-soft-dark text-danger dark:text-danger-dark flex items-center justify-center flex-shrink-0">
                  <AlertOctagon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Vencendo ≤ 15d</div>
                  <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{vencendo15}</div>
                  <div className="text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">ação urgente</div>
                </div>
              </div>
              <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-info-soft dark:bg-info-soft-dark text-info dark:text-info-dark flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Valor ativo</div>
                  <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{fmtCompactBRL(valorTotal)}</div>
                </div>
              </div>
              <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success-soft dark:bg-success-soft-dark text-success dark:text-success-dark flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Solar faturando</div>
                  <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{solarFaturando}<span className="text-[14px] font-normal text-ink-subtitle dark:text-ink-subtitle-dark">/{solarTotal}</span></div>
                  <div className="text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">com parcelas ativas</div>
                </div>
              </div>
            </div>
          );
        })()}

          {/* Área de Filtros Avançados */}
          <div className="bg-surface-alt dark:bg-surface-dark-alt/50 p-3 md:p-4 rounded-xl border border-line dark:border-line-dark">
            {/* Linha 1: Busca grande + contador + mode switcher (Lista/Cards) */}
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-0">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por número, título ou órgão do contrato..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-label dark:text-ink-label-dark text-[13.5px] rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 block pl-10 pr-3 outline-none transition-colors"
                />
              </div>
              <span className="hidden md:inline-flex items-center text-[11.5px] text-ink-subtitle dark:text-ink-subtitle-dark whitespace-nowrap tabular px-2">
                {filteredContracts.length} de {contracts.length}
              </span>
              <div className="inline-flex items-center gap-0.5 p-1 rounded-lg bg-surface dark:bg-surface-dark border border-line dark:border-line-dark flex-shrink-0" role="radiogroup" aria-label="Modo de visualização">
                {([
                  { k: 'list' as const,  label: 'Lista', Icon: List },
                  { k: 'cards' as const, label: 'Cards', Icon: CheckSquare },
                ]).map(o => {
                  const active = renderMode === o.k;
                  return (
                    <button
                      key={o.k}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      title={o.label}
                      onClick={() => setRenderMode(o.k)}
                      className={[
                        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium transition-colors',
                        active
                          ? 'bg-brand-500 text-white'
                          : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark',
                      ].join(' ')}
                    >
                      <o.Icon className="w-3 h-3" />
                      <span className="hidden lg:inline">{o.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Linha 2: Chips rápidos — VIGÊNCIA + TIPO */}
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mb-3">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Vigência</span>
              {([
                { k: 'ALL' as const,  label: 'Todos' },
                { k: '15D' as const,  label: '≤ 15 dias' },
                { k: '30D' as const,  label: '≤ 30 dias' },
                { k: '60D' as const,  label: '≤ 60 dias' },
              ]).map(c => {
                const active = quickVigencia === c.k;
                return (
                  <button
                    key={c.k}
                    type="button"
                    onClick={() => setQuickVigencia(c.k)}
                    className={[
                      'inline-flex items-center h-7 px-3 rounded-full border text-[12px] font-medium transition-colors',
                      active
                        ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
                        : 'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line dark:border-line-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                    ].join(' ')}
                  >
                    {c.label}
                  </button>
                );
              })}

              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark ml-1">Tipo</span>
              {([
                { k: 'PRICE_REGISTRATION' as const, label: 'Atas de Registro de Preço', dot: 'bg-violet-500 dark:bg-violet-400' },
                { k: 'SUPPLY' as const,             label: 'Fornecimento', dot: 'bg-info dark:bg-info-dark' },
                { k: 'SERVICE' as const,            label: 'Serviço',      dot: 'bg-brand-500' },
                { k: 'MAINTENANCE' as const,        label: 'Manutenção',   dot: 'bg-warning dark:bg-warning-dark' },
                { k: 'RENTAL' as const,             label: 'Locação',      dot: 'bg-success dark:bg-success-dark' },
                { k: 'RENTAL_MAINTENANCE' as const, label: 'Locação + Manut.', dot: 'bg-fuchsia-500 dark:bg-fuchsia-400' },
              ]).map(c => {
                const active = quickTipo === c.k;
                return (
                  <button
                    key={c.k}
                    type="button"
                    onClick={() => setQuickTipo(active ? 'ALL' : c.k)}
                    className={[
                      'inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-[12px] font-medium transition-colors',
                      active
                        ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
                        : 'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line dark:border-line-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                    ].join(' ')}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                    {c.label}
                  </button>
                );
              })}

              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <div className="relative w-44">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <Briefcase className="h-3.5 w-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  </div>
                  <select
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="w-full h-7 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-label dark:text-ink-label-dark text-[12px] rounded-full focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 block pl-8 pr-6 outline-none cursor-pointer appearance-none"
                  >
                    <option value="ALL">Empresa: Todas</option>
                    {companies.map(comp => (<option key={comp.id} value={comp.id}>{comp.corporateName}</option>))}
                  </select>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="h-7 pl-3 pr-6 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-label dark:text-ink-label-dark text-[12px] rounded-full focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none cursor-pointer appearance-none"
                >
                  <option value="ALL">Status: Todos</option>
                  <option value="ACTIVE">Status: Ativos</option>
                  <option value="INACTIVE">Status: Inativos</option>
                </select>
              </div>
            </div>

            {/* Botão para Mostrar/Ocultar Filtros Avançados */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="mt-3 w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:text-ink-title-dark bg-surface dark:bg-surface-dark hover:bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark rounded-lg transition-colors"
            >
              <Filter className="w-3.5 h-3.5" />
              {showAdvancedFilters ? 'Ocultar' : 'Mais'} Filtros
              <span className="text-ink-placeholder dark:text-ink-placeholder-dark">
                {showAdvancedFilters ? '▲' : '▼'}
              </span>
            </button>

            {/* Filtros Avançados - Colapsáveis */}
            {showAdvancedFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 mt-3 pt-3 border-t border-line dark:border-line-dark">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Building2 className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" /></div>
                  <input type="text" placeholder="Órgão Licitante..." value={organFilter} onChange={(e) => setOrganFilter(e.target.value)} className={inputIconClass} />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Layers className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" /></div>
                  <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)} className={inputIconClass}>
                    <option value="ALL">Tipo: Todos</option>
                    <option value="PRICE_REGISTRATION">Ata de Registro de Preço</option>
                    <option value="SUPPLY">Fornecimento</option>
                    <option value="SERVICE">Serviço</option>
                    <option value="MAINTENANCE">Manutenção</option>
                    <option value="RENTAL">Locação</option>
                    <option value="RENTAL_MAINTENANCE">Locação + Manutenção</option>
                    <option value="OTHER">Outros</option>
                  </select>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Shield className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" /></div>
                  <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as any)} className={inputIconClass}>
                    <option value="ALL">Categoria: Todos</option>
                    <option value="PUBLIC">Público</option>
                    <option value="PRIVATE">Privado</option>
                  </select>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Calendar className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" /></div>
                  <input type="date" placeholder="Data Início" value={startDateFilter} onChange={(e) => setStartDateFilter(e.target.value)} className={inputIconClass} />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Calendar className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" /></div>
                  <input type="date" placeholder="Data Fim" value={endDateFilter} onChange={(e) => setEndDateFilter(e.target.value)} className={inputIconClass} />
                </div>

                <div className="relative">
                  <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as any)} className={`${inputClass} appearance-none pl-3 pr-8 py-2`}>
                    <option value="ASC">Ordenar: Vencimento + Próximo</option>
                    <option value="DESC">Ordenar: Vencimento + Distante</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-ink-subtitle dark:text-ink-subtitle-dark"><ArrowUpDown className="w-4 h-4" /></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modo CARDS — pixel-perfect com .lcard (pulado quando renderMode === 'list') */}
        {renderMode === 'cards' && (
          <div className="flex-1 min-h-[18rem] p-4 md:p-6">
            {currentContracts.length === 0 ? (
              <div className="py-20 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
                <AlertCircle className="w-10 h-10 mx-auto mb-2 text-ink-placeholder dark:text-ink-placeholder-dark" />
                <p>Nenhum contrato encontrado com os filtros atuais.</p>
              </div>
            ) : (
              <div className="lic-cards">
                {currentContracts.map(contract => {
                  const daysRemaining = getDaysRemaining(contract.endDate);
                  const isActive = contract.active;
                  const tone = !isActive ? 'ok'
                    : daysRemaining < 0 ? 'past'
                    : daysRemaining <= 15 ? 'crit'
                    : daysRemaining <= 30 ? 'warn'
                    : daysRemaining <= 60 ? 'info' : 'ok';
                  const prazoColorVar = tone === 'crit' ? 'var(--color-danger)'
                    : tone === 'warn' ? 'var(--color-warning)'
                    : tone === 'info' ? 'var(--color-info)'
                    : 'var(--color-ink-title)';
                  const typeConfig = getTypeConfig(contract.type);
                  const installments = (contract as any).installments || 0;
                  const billed = (contract as any).billedInstallments || 0;
                  const billedPct = installments > 0 ? Math.round((billed / installments) * 100) : 0;
                  return (
                    <div key={contract.id} className="lcard" onClick={() => openDetailsModal(contract)}>
                      <div className={`rail ${tone === 'past' ? 'crit' : tone}`} />
                      <div className="lcard-head">
                        <span className="num">{contract.contractNumber || `#${contract.id}`}</span>
                        <span className="uf-tag" style={{ marginLeft: 6 }}>{(contract as any).category === 'PUBLIC' ? 'PUB' : (contract as any).category === 'PRIVATE' ? 'PRI' : '—'}</span>
                      </div>
                      <h4>{contract.title}</h4>
                      <div className="orgao">
                        <Building2 style={{ width: 11, height: 11, flexShrink: 0, opacity: .7 }} />
                        <span className="txt">{contract.organ || '—'}</span>
                      </div>
                      <div className="lcard-meta">
                        <div>
                          <div className="k">Vigência</div>
                          <div className="v" style={{ color: prazoColorVar, fontWeight: 600 }}>
                            {!isActive ? 'Inativo'
                              : daysRemaining < 0 ? `Vencido ${Math.abs(daysRemaining)}d`
                              : `${daysRemaining}d · ${formatCalendarDate(contract.endDate)}`}
                          </div>
                        </div>
                        <div>
                          <div className="k">Valor</div>
                          <div className="v">{formatBRLCurrency(contract.value as unknown as number)}</div>
                        </div>
                        <div>
                          <div className="k">Tipo</div>
                          <div className="v" style={{ fontSize: 12 }}>{typeConfig.label}</div>
                        </div>
                        <div>
                          <div className="k">{contract.contractType === 'SOLAR' ? 'Faturamento' : 'Início'}</div>
                          {contract.contractType === 'SOLAR' ? (
                            <div className="v" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                              <span className="tabular">{billed}/{installments || '—'}</span>
                              <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--color-surface-alt)' }}>
                                <div style={{ width: `${billedPct}%`, height: '100%', background: 'var(--color-brand-500)', borderRadius: 3, transition: 'width .3s' }} />
                              </div>
                            </div>
                          ) : (
                            <div className="v" style={{ fontSize: 12 }}>{formatCalendarDate(contract.startDate)}</div>
                          )}
                        </div>
                      </div>
                      <div className="lcard-foot">
                        <div className="pills">
                          <span className={`lic-pill ${isActive ? 'succ' : ''}`}>{isActive ? 'Ativo' : 'Inativo'}</span>
                          {contract.contractType === 'SOLAR' && <span className="lic-pill prim">Solar</span>}
                        </div>
                        <ArrowRight style={{ width: 14, height: 14, color: 'var(--color-ink-subtitle)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tabela com Scroll Horizontal em Mobile — modo LISTA (tabela completa preservada) */}
        {renderMode === 'list' && (
        <div className="flex-1 min-h-[18rem] max-h-[55vh] overflow-auto">
          <div className="min-w-full inline-block align-middle">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse relative min-w-[800px]">
            <thead className="sticky top-0 z-10 bg-surface-alt dark:bg-surface-dark-alt shadow-sm">
              <tr className="border-b border-line dark:border-line-dark">
                <th className="py-4 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">Contrato / Órgão</th>
                <th className="py-4 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider hidden lg:table-cell">Responsável</th>
                <th className="py-4 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">Valor do Contrato</th>
                <th className="py-4 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">Vigência</th>
                <th className="py-4 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">Status</th>
                <th className="py-4 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-surface dark:bg-surface-dark">
              {currentContracts.length > 0 ? (
                currentContracts.map((contract) => {
                  const daysRemaining = getDaysRemaining(contract.endDate);
                  const isActive = contract.active;

                  // Lógica de Alerta Progressivo (60 Dias)
                  const isCritical = isActive && daysRemaining >= 0 && daysRemaining <= 15; // 0-15 dias (Vermelho)
                  const isWarning = isActive && daysRemaining > 15 && daysRemaining <= 30; // 16-30 dias (Laranja)
                  const isAttention = isActive && daysRemaining > 30 && daysRemaining <= 60; // 31-60 dias (Amarelo)

                  let rowClass = "hover:bg-surface-alt dark:bg-surface-dark-alt/50 border-l-4 border-l-transparent";
                  if (isCritical) {
                    rowClass = "bg-red-50 dark:bg-red-900/30 hover:bg-red-100/60 border-l-[6px] border-red-600 shadow-sm relative z-10";
                  } else if (isWarning) {
                    rowClass = "bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100/60 border-l-[6px] border-orange-500";
                  } else if (isAttention) {
                    rowClass = "bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-100/60 border-l-[6px] border-yellow-400";
                  }

                  const companyName = getCompanyName(contract.companyId);
            const logs = auditMap[contract.id] || [];
            const createdLog = logs.find(l => l.action === 'CREATE') || logs[0];
            const updatedLog = [...logs].reverse().find(l => l.action === 'UPDATE');
            const deletedLog = logs.find(l => l.action === 'DELETE');
                  const typeConfig = getTypeConfig(contract.type);
                  // Pega o nome do primeiro contato ou N/A
                  const primaryContact = contract.contacts?.[0]?.name || 'Sem contato';

                  return (
                    <tr key={contract.id} className={`${rowClass} transition-all duration-200`}>
                      <td className="py-4 px-6">
                        <div className="flex items-start gap-3">
                          {isCritical && <div className="mt-1" title="Vencimento Crítico"><AlertOctagon className="w-6 h-6 text-red-600 dark:text-red-300 animate-pulse drop-shadow-sm" /></div>}
                          {isWarning && <div className="mt-1" title="Vencimento Próximo"><AlertTriangle className="w-6 h-6 text-orange-500 dark:text-orange-400" /></div>}
                          {isAttention && <div className="mt-1" title="Atenção Vencimento"><Hourglass className="w-6 h-6 text-yellow-500 dark:text-yellow-400" /></div>}
                          <div className="flex flex-col">
                            <span className={`font-bold ${isCritical ? 'text-red-800 dark:text-red-200' : isWarning ? 'text-orange-800 dark:text-orange-200' : isAttention ? 'text-yellow-900' : 'text-ink-title dark:text-ink-title-dark'}`}>
                              {contract.title}
                            </span>
                            <div className="flex flex-col gap-1 mt-1">
                              <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {contract.organ}
                              </span>
                              <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark flex items-center gap-1 bg-surface-alt dark:bg-surface-dark-alt px-1.5 py-0.5 rounded border border-line dark:border-line-dark">
                                  {typeConfig.label}
                                </span>
                                {createdLog && (() => {
                                  const actor = getActorFromLog(createdLog);
                                  return (
                                    <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-1 bg-surface dark:bg-surface-dark px-1.5 py-0.5 rounded border border-line dark:border-line-dark">
                                      Criado por {actor?.name || '—'}{actor?.email ? ` (${actor.email})` : ''}{actor?.timestamp ? ` em ${new Date(actor.timestamp).toLocaleDateString()}` : ''}
                                    </span>
                                  );
                                })()}
                                {updatedLog && (() => {
                                  const actor = getActorFromLog(updatedLog);
                                  return (
                                    <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-1 bg-surface dark:bg-surface-dark px-1.5 py-0.5 rounded border border-line dark:border-line-dark">
                                      Atualizado por {actor?.name || '—'}{actor?.email ? ` (${actor.email})` : ''}{actor?.timestamp ? ` em ${new Date(actor.timestamp).toLocaleDateString()}` : ''}
                                    </span>
                                  );
                                })()}
                                {deletedLog && (() => {
                                  const actor = getActorFromLog(deletedLog);
                                  return (
                                    <span className="text-[10px] text-red-600 dark:text-red-300 flex items-center gap-1 bg-surface dark:bg-surface-dark px-1.5 py-0.5 rounded border border-line dark:border-line-dark">
                                      Excluído por {actor?.name || '—'}{actor?.email ? ` (${actor.email})` : ''}{actor?.timestamp ? ` em ${new Date(actor.timestamp).toLocaleDateString()}` : ''}
                                    </span>
                                  );
                                })()}
                                <span className="text-[10px] flex items-center gap-1 text-ink-subtitle dark:text-ink-subtitle-dark">
                                  <FileText className="w-3 h-3" /> {contract.contractNumber || contract.id}
                                </span>
                                <span className={`${typeConfig.color} flex items-center gap-1`}>{typeConfig.icon} {typeConfig.label}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-1 ${
                                  contract.category === 'PRIVATE'
                                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                    : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60'
                                }`}>{contract.category === 'PRIVATE' ? '🔒 Privado' : '🏛️ Público'}</span>
                              </div>
                              {companyName && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-info-soft dark:bg-info-soft-dark text-info dark:text-info-dark border border-[rgba(43,95,168,0.25)] w-fit mt-0.5">
                                  <Briefcase className="w-2.5 h-2.5" /> {companyName}
                                </span>
                              )}
                              {(contract.file || contract.fileUrl || (contract.attachments && contract.attachments.length > 0)) && (
                                <>
                                  {console.log('Contract file/fileUrl detected:', contract.id, { file: contract.file, fileUrl: contract.fileUrl })}
                                  <button onClick={() => openAttachmentsModal(contract)} className="text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-2 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 mt-0.5 w-fit hover:bg-blue-200">
                                    <Eye className="w-3 h-3" />
                                    <Paperclip className="w-3 h-3" />
                                    <span className="ml-1 text-xs font-semibold">Anexos Complementares</span>
                                    <span className="sr-only">Ver anexos</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 hidden lg:table-cell">
                        <div className="flex items-center gap-2 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                           <User className="w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                           <span className="truncate max-w-[150px]">{primaryContact}</span>
                           {contract.contacts?.length > 1 && (
                               <span className="text-[10px] bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark px-1.5 rounded-full">+{contract.contacts.length - 1}</span>
                           )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium text-ink-text dark:text-ink-text-dark">{formatBRLCurrency(contract.value)}</span>
                          <span className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">Mensal: {contract.monthlyValue == null ? 'Não informado' : formatBRLCurrency(contract.monthlyValue)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1 text-sm">
                          <span className="text-ink-subtitle dark:text-ink-subtitle-dark text-xs">Término:</span>
                          {/* Cápsula countdown colorida — substitui os badges redundantes */}
                          {(() => {
                            const tone = !isActive ? 'ok'
                              : daysRemaining < 0 ? 'past'
                              : daysRemaining <= 15 ? 'crit'
                              : daysRemaining <= 30 ? 'warn'
                              : daysRemaining <= 60 ? 'info'
                              : 'ok';
                            return (
                              <div className={`countdown ${tone}`} style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                width: 56, height: 52, borderRadius: 8,
                                fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                                color: tone === 'ok' ? 'var(--color-ink-text)' : '#fff',
                                lineHeight: 1,
                                background: tone === 'crit' ? 'var(--color-danger)'
                                  : tone === 'warn' ? 'var(--color-warning)'
                                  : tone === 'info' ? 'var(--color-info)'
                                  : tone === 'past' ? 'var(--color-ink-placeholder)'
                                  : 'var(--color-surface-alt)',
                                alignSelf: 'flex-start',
                              }}>
                                <div style={{ fontSize: 18 }}>
                                  {!isActive ? '—' : daysRemaining < 0 ? 'EXPIR.' : daysRemaining === 0 ? '0' : Math.abs(daysRemaining)}
                                </div>
                                <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 2, opacity: .9 }}>
                                  {!isActive ? 'inat.' : daysRemaining < 0 ? '' : daysRemaining === 0 ? 'HOJE' : daysRemaining === 1 ? 'DIA' : 'DIAS'}
                                </div>
                              </div>
                            );
                          })()}

                          <div className={`flex items-center gap-1.5 mt-1 ${isCritical ? 'text-red-700 dark:text-red-300 font-semibold' : isWarning ? 'text-orange-700 dark:text-orange-300 font-semibold' : isAttention ? 'text-yellow-700 dark:text-yellow-300 font-semibold' : 'text-ink-text dark:text-ink-text-dark'}`}>
                            <Calendar className="w-3.5 h-3.5 opacity-70" />
                            <span className="text-xs tabular">{formatCalendarDate(contract.endDate)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-2">
                        {contract.active ? (
                          <span className={`${theme.classes.badge.success} inline-flex items-center gap-1.5`}>
                            <CheckCircle className="w-4 h-4" /> Ativo
                          </span>
                        ) : (
                          <span className={`${theme.classes.badge.neutral} inline-flex items-center gap-1.5`}>
                            <XCircle className="w-4 h-4" /> Inativo
                          </span>
                        )}

                        {/* Indicador de Progresso de Faturamento (Todos os contratos com parcelas) */}
                        {contract.totalMonths && contract.totalMonths > 0 && (
                          <div className="mt-1">
                            <div className="flex items-center gap-1.5 mb-1">
                              {contract.contractType === 'SOLAR' ? (
                                <svg className="w-3 h-3 text-orange-600 dark:text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                              )}
                              <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark font-medium">
                                {contract.billedInstallments || 0}/{contract.totalMonths} parcelas
                              </span>
                            </div>
                            <div className="w-full bg-surface-alt dark:bg-surface-dark-alt rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all duration-300 ${contract.contractType === 'SOLAR' ? 'bg-orange-500' : 'bg-blue-500 dark:bg-blue-600'}`}
                                style={{ width: `${((contract.billedInstallments || 0) / contract.totalMonths) * 100}%` }}
                              />
                            </div>
                            {(contract.billedInstallments || 0) === 0 && contract.active && (
                              <span className="text-[9px] text-yellow-600 font-bold mt-1 inline-block">
                                ⚠️ Sem faturamento
                              </span>
                            )}
                          </div>
                        )}

                        {contract.notes && (
                            <div className={`${theme.classes.badge.warning} flex items-center gap-1` }>
                                <StickyNote className="w-3 h-3" /> <span>Observação</span>
                            </div>
                        )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end">
                          <div className="relative">
                            <button onClick={(e) => {
                              e.stopPropagation();
                              const btn = e.currentTarget as HTMLElement;
                              const r = btn.getBoundingClientRect();
                              setActionsAnchorRect({ top: r.bottom, left: r.right });
                              setOpenActionsId(openActionsId === contract.id ? null : contract.id);
                            }} className="p-1 xl:p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt rounded-lg transition-colors actions-button" title="Mais Opções">
                              <MoreHorizontal className="w-4 h-4" />
                            </button>

                            {openActionsId === contract.id && actionsAnchorRect && createPortal(
                              <div
                                className="w-44 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md shadow-lg z-50 actions-menu"
                                style={{ position: 'fixed', top: actionsAnchorRect.top + 8, left: Math.max(actionsAnchorRect.left - 176, 8) }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* Visualizar: tenta abrir PDF se disponível, senão abre detalhes */}
                                <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); if (contract.file || contract.fileUrl || (contract.attachments && contract.attachments.length > 0)) { handleDownloadFile(contract); } else { openDetailsModal(contract); } }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><FileText className="w-4 h-4" />Visualizar</button>

                                {/* Parcelas */}
                                {contract.totalMonths && contract.totalMonths > 0 && (
                                  <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); setBillingTrackingContract(contract); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><History className="w-4 h-4" />Parcelas</button>
                                )}

                                {/* Renovar */}
                                <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); setRenewContractId(contract.id); setRenewalFile(null); setRenewalAdjustmentPercentage(''); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><RefreshCw className="w-4 h-4" />Renovar</button>

                                {/* Detalhes */}
                                <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); openDetailsModal(contract); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><FileSearch className="w-4 h-4" />Detalhes</button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-ink-subtitle dark:text-ink-subtitle-dark">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <AlertCircle className="w-8 h-8 text-ink-placeholder dark:text-ink-placeholder-dark" />
                      <p>Nenhum contrato encontrado com os filtros atuais.</p>
                      <button onClick={clearFilters} className="text-blue-600 dark:text-blue-300 hover:underline text-sm">Limpar Filtros</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
            </div>
          </div>
        </div>
        )}
        {/* ... Footer Pagination (Mantido igual) ... */}
        <div className="p-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt flex flex-col sm:flex-row justify-between items-center gap-4 text-sm flex-shrink-0">
          <div className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark">
            <span>Itens por página:</span>
            <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark text-sm rounded-md focus:ring-brand-500 focus:border-brand-500 block p-1 outline-none">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="hidden sm:inline text-ink-placeholder dark:text-ink-placeholder-dark">|</span>
            <span className="hidden sm:inline">Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, filteredContracts.length)} - {Math.min(currentPage * itemsPerPage, filteredContracts.length)} de {filteredContracts.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 border border-line-strong dark:border-line-dark-strong rounded-md hover:bg-surface dark:bg-surface-dark disabled:opacity-50 disabled:hover:bg-transparent transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-ink-subtitle dark:text-ink-subtitle-dark font-medium">Página {currentPage} de {totalPages || 1}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 border border-line-strong dark:border-line-dark-strong rounded-md hover:bg-surface dark:bg-surface-dark disabled:opacity-50 disabled:hover:bg-transparent transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Modal de Detalhes e Edição */}
      {selectedContract && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">

            {/* Modal Header */}
            <div className={`p-6 border-b border-line dark:border-line-dark flex justify-between items-start ${isEditing ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-surface-alt dark:bg-surface-dark-alt/50'}`}>
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2 mb-2">
                   {!isEditing && (
                       <>
                        {selectedContract.active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/60"><CheckCircle className="w-3 h-3" /> Ativo</span>
                            ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark border border-line dark:border-line-dark"><XCircle className="w-3 h-3" /> Inativo</span>
                        )}

                        {/* Tags de Alerta no Modal */}
                        {selectedContract.active && getDaysRemaining(selectedContract.endDate) <= 15 && getDaysRemaining(selectedContract.endDate) >= 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white shadow-sm animate-pulse"><AlertOctagon className="w-3 h-3" /> Vence em {getDaysRemaining(selectedContract.endDate)} dias</span>
                        )}
                        {selectedContract.active && getDaysRemaining(selectedContract.endDate) > 15 && getDaysRemaining(selectedContract.endDate) <= 30 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-500 text-white shadow-sm"><AlertTriangle className="w-3 h-3" /> Vence em {getDaysRemaining(selectedContract.endDate)} dias</span>
                        )}
                        {selectedContract.active && getDaysRemaining(selectedContract.endDate) > 30 && getDaysRemaining(selectedContract.endDate) <= 60 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900 border border-yellow-500 shadow-sm"><Hourglass className="w-3 h-3" /> Vence em {getDaysRemaining(selectedContract.endDate)} dias</span>
                        )}
                       </>
                   )}
                   {isEditing && (
                       <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 uppercase tracking-wider">
                           <Edit2 className="w-3 h-3" /> Editando Contrato
                       </span>
                   )}
                </div>

                {isEditing ? (
                    <input type="text" value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} className="w-full text-lg font-bold text-ink-title dark:text-ink-title-dark bg-surface dark:bg-surface-dark border border-blue-300 rounded px-2 py-1 focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Título do Contrato" />
                ) : (
                    <h3 className="text-xl font-bold text-ink-title dark:text-ink-title-dark leading-tight">{selectedContract.title}</h3>
                )}

                {isEditing && editForm ? (
                  <div className="mt-2">
                    <label className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-1">Número do Contrato</label>
                    <input type="text" value={editForm.contractNumber || ''} onChange={e => setEditForm({...editForm, contractNumber: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Ex: 276/2025" />
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-1 bg-surface dark:bg-surface-dark px-2 py-1 rounded border border-line dark:border-line-dark">
                    <FileText className="w-3.5 h-3.5" /> Contrato: <span className="font-mono">{selectedContract.contractNumber || selectedContract.id}</span>
                  </span>

                  {!isEditing && (
                    <button onClick={() => navigate('/ai-analysis', { state: { tenderId: selectedContract.tenderId } })} className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 rounded-lg text-sm font-medium transition-all shadow-sm" title="Clique para analisar o edital desta licitação com IA">
                        <Sparkles className="w-3.5 h-3.5" /> Licitação #{selectedContract.tenderId} <ExternalLink className="w-3 h-3 opacity-50" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                  {!isEditing && (
              <button onClick={() => setIsEditing(true)} className={`${theme.classes.button.outline} flex items-center gap-1 px-3 py-1.5 text-sm`}>
              <Edit2 className="w-4 h-4" /> Editar
            </button>
                  )}
                  <button onClick={() => setSelectedContract(null)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt p-2 rounded-lg transition-colors">
                    <X className="w-5 h-5" />
                  </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Órgão */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Órgão Contratante</span>
                  {isEditing ? (
                      <input type="text" value={editForm.organ} onChange={e => setEditForm({...editForm, organ: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none" />
                  ) : (
                    <div className="flex items-center gap-2 text-ink-title dark:text-ink-title-dark font-medium">
                        <Building2 className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /> {selectedContract.organ}
                    </div>
                  )}
                </div>

                {/* Valor */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Valor do Contrato</span>
          {isEditing ? (
            <div className="relative">
              <span className="absolute left-2 top-1.5 text-ink-subtitle dark:text-ink-subtitle-dark text-sm">R$</span>
              <MoneyInput
                className="w-full border border-line-strong dark:border-line-dark-strong rounded pl-8 px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                value={typeof editForm.value === 'number' ? editForm.value : null}
                onValueChange={(v) => setEditForm({...editForm, value: v ?? undefined})}
              />
            </div>
          ) : (
          <div className="flex items-center gap-2 text-ink-title dark:text-ink-title-dark font-medium">
            <DollarSign className="w-4 h-4 text-green-600 dark:text-green-300" />
            {formatBRLCurrency(selectedContract.value)}
          </div>
          )}
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Valor Mensal Vigente</span>
                  {isEditing ? (
                    <div className="relative">
                      <span className="absolute left-2 top-1.5 text-ink-subtitle dark:text-ink-subtitle-dark text-sm">R$</span>
                      <MoneyInput
                        className="w-full border border-line-strong dark:border-line-dark-strong rounded pl-8 px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                        value={typeof editForm.monthlyValue === 'number' ? editForm.monthlyValue : null}
                        onValueChange={(v) => setEditForm({...editForm, monthlyValue: v ?? undefined})}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-ink-title dark:text-ink-title-dark font-medium">
                      <DollarSign className="w-4 h-4 text-info dark:text-info-dark" />
                      {selectedContract.monthlyValue == null ? 'Não informado' : formatBRLCurrency(selectedContract.monthlyValue)}
                    </div>
                  )}
                </div>

                {/* Data Início */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Início da Vigência</span>
                  {isEditing ? (
                      <input type="date" value={editForm.startDate} onChange={e => setEditForm({...editForm, startDate: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none" />
                  ) : (
                    <div className="flex items-center gap-2 text-ink-text dark:text-ink-text-dark">
                        <Calendar className="w-4 h-4 text-blue-500 dark:text-blue-400" /> {formatCalendarDate(selectedContract.startDate)}
                    </div>
                  )}
                </div>

                {/* Data Fim */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Fim da Vigência</span>
          {isEditing ? (
            <input type="date" value={editForm.endDate} onChange={e => {
              const newDate = e.target.value;
              // Atualiza também o campo active localmente para feedback imediato (comparação em UTC)
              const todayIso = new Date().toISOString().split('T')[0];
              const todayStartUtc = parseDateOnlyToUTC(todayIso);
              const newEndUtc = parseDateOnlyToUTC(newDate);
              const computedActive = newEndUtc && todayStartUtc ? newEndUtc >= todayStartUtc : true;
              setEditForm({...editForm, endDate: newDate, active: computedActive});
            }} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none" />
          ) : (
                    <div className="flex items-center gap-2 text-ink-text dark:text-ink-text-dark">
                        <Clock className="w-4 h-4 text-orange-500 dark:text-orange-400" /> {formatCalendarDate(selectedContract.endDate)}
                    </div>
                  )}
                </div>

                {/* Data Assinatura */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Data de Assinatura</span>
                  {isEditing ? (
                      <input type="date" value={editForm.signatureDate || ''} onChange={e => setEditForm({...editForm, signatureDate: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none" />
                  ) : (
                    <div className="flex items-center gap-2 text-ink-text dark:text-ink-text-dark">
                        <PenTool className="w-4 h-4 text-purple-500" />
                        {selectedContract.signatureDate ? formatCalendarDate(selectedContract.signatureDate) : <span className="text-ink-placeholder dark:text-ink-placeholder-dark italic">Não informado</span>}
                    </div>
                  )}
                </div>

                {/* Licitação de Origem (Novo Campo) */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Licitação de Origem</span>
                  <div
                    onClick={() => !isEditing && navigate('/ai-analysis', { state: { tenderId: selectedContract.tenderId } })}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors w-fit ${isEditing ? 'text-ink-subtitle dark:text-ink-subtitle-dark cursor-default' : 'text-indigo-600 dark:text-indigo-300 hover:text-indigo-800 hover:underline cursor-pointer'}`}
                    title={isEditing ? '' : "Clique para ver a análise da licitação"}
                  >
                    <FileSearch className={`w-4 h-4 ${isEditing ? 'text-ink-placeholder dark:text-ink-placeholder-dark' : 'text-indigo-500'}`} />
                    ID: {selectedContract.tenderId}
                    {!isEditing && <ExternalLink className="w-3 h-3 opacity-50" />}
                  </div>
                </div>

                {/* Status (Apenas em Edição) */}
                {isEditing && (
                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Status do Contrato</span>
                        <select value={editForm.active ? 'true' : 'false'} onChange={e => setEditForm({...editForm, active: e.target.value === 'true'})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark">
                            <option value="true">Ativo</option>
                            <option value="false">Inativo</option>
                        </select>
                    </div>
                )}
              </div>

              {/* SEÇÃO DE CONTATOS (SUBSTITUI RESPONSÁVEL) */}
              <div className="mt-6 pt-4 border-t border-line dark:border-line-dark">
                  <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider">Contatos e Responsáveis</span>
                      {isEditing && (
                          <button onClick={handleAddContact} type="button" className="text-xs flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 px-2 py-1 rounded transition-colors">
                              <Plus className="w-3 h-3" /> Adicionar
                          </button>
                      )}
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                      {(isEditing ? editForm.contacts : selectedContract.contacts)?.map((contact, idx) => (
                          <div key={contact.id || idx} className={`p-3 rounded-lg border ${isEditing ? 'border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-900/30/50' : 'border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt'}`}>
                              {isEditing ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <input
                                          type="text"
                                          placeholder="Nome Completo"
                                          className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-xs outline-none focus:border-brand-500"
                                          value={contact.name}
                                          onChange={(e) => handleContactChange(contact.id, 'name', e.target.value)}
                                      />
                                      <div className="flex gap-2">
                                          <input
                                              type="text"
                                              placeholder="Cargo / Função"
                                              className="flex-1 border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-xs outline-none focus:border-brand-500"
                                              value={contact.role || ''}
                                              onChange={(e) => handleContactChange(contact.id, 'role', e.target.value)}
                                          />
                                          <button onClick={() => handleRemoveContact(contact.id)} className="text-red-500 dark:text-red-400 hover:text-red-700 p-1">
                                              <Trash2 className="w-4 h-4" />
                                          </button>
                                      </div>
                                      <input
                                          type="text"
                                          placeholder="E-mail"
                                          className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-xs outline-none focus:border-brand-500"
                                          value={contact.email || ''}
                                          onChange={(e) => handleContactChange(contact.id, 'email', e.target.value)}
                                      />
                                      <input
                                          type="text"
                                          placeholder="Telefone"
                                          className="w-full border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-xs outline-none focus:border-brand-500"
                                          value={contact.phone || ''}
                                          onChange={(e) => handleContactChange(contact.id, 'phone', e.target.value)}
                                      />
                                  </div>
                              ) : (
                                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                                      <div className="flex items-center gap-3">
                                          <div className="w-8 h-8 rounded-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark flex items-center justify-center text-ink-subtitle dark:text-ink-subtitle-dark">
                                              <UserCheck className="w-4 h-4" />
                                          </div>
                                          <div>
                                              <p className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">{contact.name}</p>
                                              <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{contact.role || 'Responsável'}</p>
                                          </div>
                                      </div>
                                      <div className="flex flex-col md:flex-row gap-2 md:gap-4 text-xs text-ink-subtitle dark:text-ink-subtitle-dark pl-11 md:pl-0">
                                          {contact.email && (
                                              <div className="flex items-center gap-1.5">
                                                  <Mail className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                                                  <a href={`mailto:${contact.email}`} className="hover:underline hover:text-blue-700">{contact.email}</a>
                                              </div>
                                          )}
                                          {contact.phone && (
                                              <div className="flex items-center gap-1.5">
                                                  <Phone className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                                                  <span>{contact.phone}</span>
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              )}
                          </div>
                      ))}
                      {(!selectedContract.contacts || selectedContract.contacts.length === 0) && !isEditing && (
                          <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark italic p-2">Nenhum contato cadastrado.</div>
                      )}
                  </div>
              </div>

              {/* Seção da Empresa e Tipo Vinculada */}
              <div className="mt-6 pt-4 border-t border-line dark:border-line-dark">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                          <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider block mb-2">Empresa Operadora</span>
                          <div className={`flex items-center gap-3 p-3 rounded-lg border ${isEditing ? 'bg-surface dark:bg-surface-dark border-line-strong dark:border-line-dark-strong' : 'bg-surface-alt dark:bg-surface-dark-alt border-line dark:border-line-dark'}`}>
                              <Briefcase className="w-5 h-5 text-ink-subtitle dark:text-ink-subtitle-dark" />
                              <select
                                  value={isEditing ? (editForm.companyId || '') : (selectedContract.companyId || '')}
                                  onChange={(e) => isEditing ? setEditForm({...editForm, companyId: e.target.value}) : handleSaveDetails(e.target.value, undefined)}
                                  className={`bg-transparent border-none text-sm font-medium text-ink-title dark:text-ink-title-dark focus:ring-0 w-full cursor-pointer outline-none ${!isEditing && 'appearance-none pointer-events-none'}`}
                                  disabled={!isEditing && false}
                              >
                                  <option value="">Selecione a empresa responsável...</option>
                                  {companies.map(c => (
                                      <option key={c.id} value={c.id}>{c.corporateName} ({c.cnpj})</option>
                                  ))}
                              </select>
                          </div>
                      </div>

                      <div>
                          <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider block mb-2">Tipo de Contrato</span>
                          <div className={`flex items-center gap-3 p-3 rounded-lg border ${isEditing ? 'bg-surface dark:bg-surface-dark border-line-strong dark:border-line-dark-strong' : 'bg-surface-alt dark:bg-surface-dark-alt border-line dark:border-line-dark'}`}>
                              <Layers className="w-5 h-5 text-ink-subtitle dark:text-ink-subtitle-dark" />
                              <select
                                  value={isEditing ? (editForm.type || 'OTHER') : (selectedContract.type || 'OTHER')}
                                  onChange={(e) => isEditing ? setEditForm({...editForm, type: e.target.value as ContractType}) : handleSaveDetails(undefined, e.target.value as ContractType, undefined)}
                                  className={`bg-transparent border-none text-sm font-medium text-ink-title dark:text-ink-title-dark focus:ring-0 w-full cursor-pointer outline-none ${!isEditing && 'appearance-none pointer-events-none'}`}
                              >
                                  <option value="PRICE_REGISTRATION">Ata de Registro de Preço</option>
                                  <option value="SUPPLY">Fornecimento</option>
                                  <option value="SERVICE">Serviço</option>
                                  <option value="MAINTENANCE">Manutenção</option>
                  <option value="RENTAL">Locação</option>
                  <option value="RENTAL_MAINTENANCE">Locação + Manutenção</option>
                                  <option value="OTHER">Outros</option>
                              </select>
                          </div>
                      </div>

                      <div>
                          <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider block mb-2">Categoria</span>
                          <div className={`flex items-center gap-3 p-3 rounded-lg border ${isEditing ? 'bg-surface dark:bg-surface-dark border-line-strong dark:border-line-dark-strong' : 'bg-surface-alt dark:bg-surface-dark-alt border-line dark:border-line-dark'}`}>
                              <Layers className="w-5 h-5 text-ink-subtitle dark:text-ink-subtitle-dark" />
                              <select
                                  value={isEditing ? (editForm.category || 'PUBLIC') : (selectedContract.category || 'PUBLIC')}
                                  onChange={(e) => isEditing ? setEditForm({...editForm, category: e.target.value}) : handleSaveDetails(undefined, undefined, e.target.value)}
                                  className={`bg-transparent border-none text-sm font-medium text-ink-title dark:text-ink-title-dark focus:ring-0 w-full cursor-pointer outline-none ${!isEditing && 'appearance-none pointer-events-none'}`}
                              >
                                  <option value="PUBLIC">Público</option>
                                  <option value="PRIVATE">Privado</option>
                              </select>
                          </div>
                      </div>

                      <div>
                          <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider block mb-2">Classificação do Contrato</span>
                          <div className={`flex items-center gap-3 p-3 rounded-lg border ${isEditing ? 'bg-surface dark:bg-surface-dark border-line-strong dark:border-line-dark-strong' : 'bg-surface-alt dark:bg-surface-dark-alt border-line dark:border-line-dark'}`}>
                              <svg className="w-5 h-5 text-ink-subtitle dark:text-ink-subtitle-dark" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 7h18M3 12h18M3 17h18" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              {isEditing ? (
                                <select
                                  value={editForm.contractType || 'STANDARD'}
                                  onChange={(e) => setEditForm({...editForm, contractType: e.target.value as 'STANDARD' | 'SOLAR'})}
                                  className={`bg-transparent border-none text-sm font-medium text-ink-title dark:text-ink-title-dark focus:ring-0 w-full cursor-pointer outline-none`}
                                >
                                  <option value="STANDARD">📄 Contrato Padrão</option>
                                  <option value="SOLAR">☀️ Contrato Solar (Locação + Comodato)</option>
                                </select>
                              ) : (
                                <div className="text-sm font-medium text-ink-title dark:text-ink-title-dark">
                                  {selectedContract.contractType === 'SOLAR' ? '☀️ Contrato Solar' : '📄 Contrato Padrão'}
                                </div>
                              )}
                          </div>
                      </div>
                  </div>
              </div>

              {/* Seção de Acompanhamento de Parcelas (Edição) */}
              {isEditing && (
                <div className="mt-6 pt-4 border-t border-line dark:border-line-dark">
                  <div className={`border rounded-lg p-4 ${editForm.contractType === 'SOLAR' ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800/60' : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/60'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {editForm.contractType === 'SOLAR' ? (
                        <svg className="w-5 h-5 text-orange-600 dark:text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      )}
                      <h4 className={`text-sm font-bold ${editForm.contractType === 'SOLAR' ? 'text-orange-800 dark:text-orange-200' : 'text-blue-900'}`}>
                        Acompanhamento de Parcelas {editForm.contractType !== 'SOLAR' && '(Opcional)'}
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={`block text-xs font-semibold uppercase mb-1 ${editForm.contractType === 'SOLAR' ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'}`}>
                          Total de Meses (Parcelas)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="240"
                          value={editForm.totalMonths || ''}
                          onChange={e => setEditForm({...editForm, totalMonths: e.target.value ? parseInt(e.target.value) : undefined})}
                          className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 outline-none bg-surface dark:bg-surface-dark ${
                            editForm.contractType === 'SOLAR'
                              ? 'border-orange-300 focus:ring-orange-500'
                              : 'border-blue-300 focus:ring-brand-500'
                          }`}
                          placeholder={editForm.contractType === 'SOLAR' ? '48' : 'Ex: 12, 24, 36...'}
                        />
                        <p className={`text-[10px] mt-1 ${editForm.contractType === 'SOLAR' ? 'text-orange-600 dark:text-orange-300' : 'text-blue-600 dark:text-blue-300'}`}>
                          {editForm.contractType === 'SOLAR'
                            ? 'Típico: 48 meses (4 anos)'
                            : 'Deixe vazio se não parcelado'}
                        </p>
                      </div>

                      <div>
                        <label className={`block text-xs font-semibold uppercase mb-1 ${editForm.contractType === 'SOLAR' ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'}`}>
                          Parcelas Já Faturadas
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={editForm.billedInstallments || 0}
                          onChange={e => setEditForm({...editForm, billedInstallments: parseInt(e.target.value)})}
                          className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 outline-none bg-surface dark:bg-surface-dark ${
                            editForm.contractType === 'SOLAR'
                              ? 'border-orange-300 focus:ring-orange-500'
                              : 'border-blue-300 focus:ring-brand-500'
                          }`}
                          placeholder="0"
                          disabled={!editForm.totalMonths}
                        />
                        <p className={`text-[10px] mt-1 ${editForm.contractType === 'SOLAR' ? 'text-orange-600 dark:text-orange-300' : 'text-blue-600 dark:text-blue-300'}`}>
                          Deixe 0 se ainda não foi faturado
                        </p>
                      </div>

                      <div>
                        <label className={`block text-xs font-semibold uppercase mb-1 ${editForm.contractType === 'SOLAR' ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'}`}>
                          Data do 1º Faturamento
                        </label>
                        <input
                          type="date"
                          value={editForm.firstBillingDate ? new Date(editForm.firstBillingDate).toISOString().split('T')[0] : ''}
                          onChange={e => setEditForm({...editForm, firstBillingDate: e.target.value})}
                          className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 outline-none ${
                            editForm.contractType === 'SOLAR'
                              ? 'border-orange-300 focus:ring-orange-500'
                              : 'border-blue-300 focus:ring-brand-500'
                          }`}
                          disabled={!editForm.totalMonths}
                        />
                        <p className={`text-[10px] mt-1 ${editForm.contractType === 'SOLAR' ? 'text-orange-600 dark:text-orange-300' : 'text-blue-600 dark:text-blue-300'}`}>
                          Quando começou/começará
                        </p>
                      </div>
                    </div>

                    <div className={`mt-3 p-3 bg-surface dark:bg-surface-dark rounded border ${editForm.contractType === 'SOLAR' ? 'border-orange-200 dark:border-orange-800/60' : 'border-blue-200 dark:border-blue-800/60'}`}>
                      <p className={`text-xs ${editForm.contractType === 'SOLAR' ? 'text-orange-800 dark:text-orange-200' : 'text-blue-800 dark:text-blue-200'}`}>
                        {editForm.contractType === 'SOLAR' ? (
                          <><strong>ℹ️ Contratos Solares:</strong> Locação + comodato com 48 parcelas típicas. Sistema acompanha faturamento automaticamente.</>
                        ) : (
                          <><strong>ℹ️ Parcelas:</strong> Contratos vigentes sem parcelas pagas aparecerão em "Vigentes sem Faturamento".</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Descrição e Resumo IA */}
              <div className="mt-8">
                <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider block mb-2">Descrição Completa</span>
                {isEditing ? (
                    <textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="w-full bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg p-3 text-sm text-ink-text dark:text-ink-text-dark focus:ring-2 focus:ring-brand-500 outline-none resize-none h-32" />
                ) : (
                    <div className="bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-4 border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm leading-relaxed mb-4">
                        {selectedContract.description}
                    </div>
                )}

                {/* Bloco de Resumo IA (Apenas View Mode) */}
                {!isEditing && (isSummarizing || aiSummary) && (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-100 shadow-sm relative overflow-hidden">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="bg-purple-100 p-1.5 rounded-lg text-purple-600 dark:text-purple-300"><Sparkles className="w-4 h-4" /></div>
                            <span className="text-sm font-bold text-purple-900">Análise Inteligente do Contrato</span>
                        </div>
                        {isSummarizing ? (
                            <div className="flex items-center gap-2 text-sm text-purple-700 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Analisando cláusulas e escopo...</div>
                        ) : (
                            <div className="text-sm text-ink-text dark:text-ink-text-dark leading-relaxed whitespace-pre-wrap font-medium">{aiSummary}</div>
                        )}
                        <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-purple-200 rounded-full opacity-20 blur-xl pointer-events-none"></div>
                    </div>
                )}
              </div>

              {/* Histórico de Renovações (Apenas View Mode) */}
              {!isEditing && selectedContract.renewalHistory && selectedContract.renewalHistory.length > 0 && (
                <div className="mt-8">
                  <span className="text-xs font-semibold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider block mb-3 flex items-center gap-1">
                    <History className="w-3 h-3" /> Histórico de Renovações
                  </span>
                  <div className="bg-blue-50 dark:bg-blue-900/30/50 rounded-lg border border-blue-100 p-4">
                    <ul className="space-y-3">
                      {selectedContract.renewalHistory.map((record, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm">
                          <div className="mt-0.5 min-w-4 h-4 rounded-full bg-blue-200 border border-blue-300 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-500"></div>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-ink-title dark:text-ink-title-dark font-medium">Renovação Contratual</span>
                            <span className="text-ink-subtitle dark:text-ink-subtitle-dark text-xs flex flex-wrap gap-2 items-center">
                              <span>Realizada em <span className="font-medium text-ink-text dark:text-ink-text-dark">{formatCalendarDate(record.renewalDate)}</span></span>
                              <span>•</span>
                              <span>Nova vigência até <span className="font-medium text-blue-700 dark:text-blue-300">{formatCalendarDate(record.newEndDate)}</span></span>
                              {record.adjustmentPercentage != null && (
                                <>
                                  <span>•</span>
                                  <span>Reajuste de <span className="font-medium text-ink-text dark:text-ink-text-dark">{record.adjustmentPercentage.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%</span></span>
                                </>
                              )}
                              {record.previousMonthlyValue != null && record.newMonthlyValue != null && (
                                <span>Mensal: <span className="font-medium text-ink-text dark:text-ink-text-dark">{formatBRLCurrency(record.previousMonthlyValue)}</span> para <span className="font-medium text-green-700 dark:text-green-300">{formatBRLCurrency(record.newMonthlyValue)}</span></span>
                              )}
                              {record.file && (
                                <button onClick={() => handleDownloadRenewalFile(record.file!)} className="flex items-center gap-1 ml-2 text-xs bg-surface dark:bg-surface-dark px-2 py-0.5 rounded border border-blue-200 dark:border-blue-800/60 text-blue-600 dark:text-blue-300 hover:bg-blue-50 transition-colors" title="Baixar Arquivo do Aditivo">
                                    <Paperclip className="w-3 h-3" /> Aditivo
                                </button>
                              )}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Seção de Anexo do Contrato */}
              {/* debug info removed from JSX to satisfy ReactNode types */}
              {!isEditing && (selectedContract.file || selectedContract.fileUrl) && (
                <div className="mt-8 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                      <Paperclip className="w-4 h-4" /> Arquivo do Contrato
                    </span>
                  </div>
                  <div className="bg-surface dark:bg-surface-dark rounded-lg border border-blue-200 dark:border-blue-800/60 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-red-600 dark:text-red-300" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-ink-title dark:text-ink-title-dark">
                            {selectedContract.file?.name || selectedContract.title + '.pdf'}
                          </span>
                          <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            {selectedContract.file ? (selectedContract.file.size / 1024).toFixed(2) + ' KB' : 'Arquivo disponível'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadFile(selectedContract)}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium shadow-sm"
                        title="Visualizar PDF do Contrato"
                      >
                        <FileText className="w-4 h-4" />
                        Visualizar PDF
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload de Arquivo do Contrato (apenas em edição) */}
              {isEditing && (
                <div className="mt-8 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                      <Paperclip className="w-4 h-4" /> Anexar Arquivo do Contrato
                    </span>
                  </div>
                  <div className="bg-surface dark:bg-surface-dark rounded-lg border border-blue-200 dark:border-blue-800/60 p-4">
                    <label className="flex flex-col items-center gap-3 cursor-pointer group">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf"
                        onChange={(e) => setContractFile(e.target.files ? e.target.files[0] : null)}
                      />
                      <div className="w-full flex items-center justify-between p-3 bg-surface-alt dark:bg-surface-dark-alt rounded-lg border-2 border-dashed border-line-strong dark:border-line-dark-strong group-hover:border-blue-400 group-hover:bg-blue-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
                            <Upload className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-ink-title dark:text-ink-title-dark">
                              {contractFile ? contractFile.name : (selectedContract.fileUrl || selectedContract.file) ? 'Alterar arquivo anexado' : 'Selecionar arquivo PDF'}
                            </span>
                            <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                              {contractFile ? `${(contractFile.size / 1024).toFixed(2)} KB` : 'Clique para fazer upload'}
                            </span>
                          </div>
                        </div>
                        {(contractFile || selectedContract.fileUrl || selectedContract.file) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setContractFile(null);
                            }}
                            className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-500 p-1.5 hover:bg-red-50 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </label>
                    {(selectedContract.fileUrl || selectedContract.file) && !contractFile && (
                      <div className="mt-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Arquivo atual: {selectedContract.file?.name || selectedContract.title + '.pdf'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bloco de Notas / Observações */}
              <div className="mt-8 bg-amber-50 rounded-xl border border-amber-100 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-amber-800 flex items-center gap-2"><StickyNote className="w-4 h-4" /> Bloco de Notas do Contrato</span>
                  <span className="text-[10px] text-amber-600 uppercase tracking-wider font-medium">Uso Interno</span>
                </div>
                <textarea value={isEditing ? (editForm.notes || '') : noteInput} onChange={(e) => isEditing ? setEditForm({...editForm, notes: e.target.value}) : setNoteInput(e.target.value)} placeholder="Escreva observações importantes, pendências ou detalhes da negociação aqui..." className="w-full bg-surface dark:bg-surface-dark border border-amber-200 rounded-lg p-3 text-sm text-ink-text dark:text-ink-text-dark focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 outline-none resize-none min-h-[100px]" />
                {!isEditing && (
                    <div className="flex justify-end mt-3">
            <button onClick={() => handleSaveDetails()} disabled={isSavingNote || noteInput === (selectedContract.notes || '')} className={isSavingNote || noteInput === (selectedContract.notes || '') ? 'opacity-50 cursor-not-allowed px-4 py-2 rounded-lg text-sm font-medium' : `${theme.classes.button.primary} px-4 py-2 text-sm font-medium`}>
              <Save className="w-4 h-4" /> {isSavingNote ? 'Salvando...' : 'Salvar Observação'}
            </button>
                    </div>
                )}
              </div>
            </div>

            {/* Anexos Complementares (quando em edição) */}
            {isEditing && (
              <div className="p-6 border-t border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
                <h4 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-3 flex items-center gap-2"><Paperclip className="w-4 h-4" /> Anexos Complementares</h4>

                <div className="flex items-center gap-3 mb-3">
                  <label className="flex-1 cursor-pointer bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong border-dashed rounded-lg p-2 text-center hover:bg-blue-50 transition-colors">
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple onChange={handleAddAttachments} />
                    <div className="flex items-center justify-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                      <Upload className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /> Selecionar arquivos
                    </div>
                  </label>
                  <button onClick={handleUploadAttachments} disabled={attachmentsUploading || newAttachments.length === 0} className={`px-3 py-2 rounded text-sm ${attachmentsUploading || newAttachments.length === 0 ? 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark' : 'bg-brand-500 text-white hover:bg-brand-600'}`}>
                    {attachmentsUploading ? 'Enviando...' : 'Enviar Anexos'}
                  </button>
                </div>

                {newAttachments.length > 0 && (
                  <div className="mb-3 text-sm">
                    <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">Arquivos selecionados (prontos para enviar):</div>
                    <div className="space-y-2">
                      {newAttachments.map((f, i) => (
                        <div key={i} className="flex items-center justify-between gap-3">
                          <div className="truncate"><div className="text-sm">{f.name}</div><div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{f.type} • {(f.size/1024).toFixed(2)} KB</div></div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleRemoveNewAttachment(i)} className="text-xs text-red-600 dark:text-red-300">Remover</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">Anexos já salvos:</div>
                  {((editForm && editForm.attachments) || []).length > 0 ? (
                    <div className="space-y-3">
                      {((editForm && editForm.attachments) || []).map((att: any) => (
                        <div key={att.id} className="flex items-center justify-between gap-3">
                          <div className="truncate">
                            <div className="text-sm font-medium">{att.name}</div>
                            <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{att.type} {att.size ? `• ${(att.size/1024).toFixed(2)} KB` : ''}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleDownloadAttachment(att)} className="px-2 py-1 bg-brand-500 text-white rounded text-xs">Visualizar</button>
                            <button onClick={async () => { await handleDeleteAttachment(att.id); }} className="px-2 py-1 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 rounded text-xs border border-red-100 dark:border-red-800/50">Remover</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhum anexo complementar encontrado para este contrato.</div>
                  )}
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="p-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt flex justify-between gap-3">
              {isEditing ? (
                  <>
          <button onClick={handleDeleteContract} className={`${theme.classes.button.danger} flex items-center gap-2 px-4 py-2 text-sm`}>
            <Trash2 className="w-4 h-4" /> Excluir
          </button>
                    <div className="flex gap-3">
            <button onClick={() => { setIsEditing(false); setEditForm(selectedContract); }} className={`${theme.classes.button.outline} px-4 py-2 text-sm`}>Cancelar Edição</button>
            <button onClick={handleSaveEdit} className={`${theme.classes.button.primary} px-6 py-2 flex items-center gap-2 text-sm`}>
              <Save className="w-4 h-4" /> Salvar Alterações
            </button>
                    </div>
                  </>
              ) : (
                  <div className="flex justify-end w-full">
                      <button onClick={() => setSelectedContract(null)} className="px-4 py-2 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt font-medium text-sm shadow-sm transition-colors">Fechar</button>
                  </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Renovação */}
      {contractToRenew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-full mb-4 mx-auto"><RefreshCw className="w-6 h-6 text-green-600 dark:text-green-300" /></div>
              <h3 className="text-lg font-bold text-center text-ink-title dark:text-ink-title-dark mb-2">Renovar Contrato?</h3>
              <p className="text-center text-ink-subtitle dark:text-ink-subtitle-dark text-sm mb-6">
                Você está prestes a renovar o contrato <strong>{contractToRenew.title}</strong> por mais 1 ano.<br /><br />
                A nova data de vencimento será: <br/><strong className="text-ink-title dark:text-ink-title-dark">{(() => { const d = parseCalendarDate(contractToRenew.endDate); if (!d) return 'Data inválida'; d.setUTCFullYear(d.getUTCFullYear() + 1); return formatCalendarDate(d.toISOString()); })()}</strong>
              </p>
              <div className="mb-6 bg-surface-alt dark:bg-surface-dark-alt p-4 rounded-lg border border-line dark:border-line-dark">
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-ink-text dark:text-ink-text-dark mb-1">Percentual de Reajuste Anual (%)</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="-99.9999"
                      max="1000"
                      step="0.0001"
                      value={renewalAdjustmentPercentage}
                      onChange={(event) => setRenewalAdjustmentPercentage(event.target.value)}
                      placeholder="Ex.: 4,50"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                    />
                    {contractToRenew.monthlyValue == null ? (
                      <p className="mt-2 text-xs text-danger dark:text-danger-dark">Cadastre o valor mensal vigente no contrato antes de renovar.</p>
                    ) : (
                      <p className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                        Valor mensal atual: <strong>{formatBRLCurrency(contractToRenew.monthlyValue)}</strong>
                        {renewalMonthlyValuePreview != null && <>. Novo valor: <strong className="text-green-700 dark:text-green-300">{formatBRLCurrency(renewalMonthlyValuePreview)}</strong></>}
                      </p>
                    )}
                  </div>
                  <label className="block text-xs font-semibold text-ink-text dark:text-ink-text-dark mb-2 flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> Anexar Termo Aditivo (Opcional)</label>
                  <div className="flex items-center gap-2">
                      <label className="flex-1 cursor-pointer bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong border-dashed rounded-lg p-2 text-center hover:bg-blue-50 transition-colors">
                          <input type="file" className="hidden" accept=".pdf" onChange={(e) => setRenewalFile(e.target.files ? e.target.files[0] : null)} />
                          <div className="flex items-center justify-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                              <Upload className="w-3.5 h-3.5" /> {renewalFile ? <span className="text-blue-600 dark:text-blue-300 font-medium truncate max-w-[150px]">{renewalFile.name}</span> : "Selecionar PDF"}
                          </div>
                      </label>
                      {renewalFile && (<button onClick={() => setRenewalFile(null)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-500 p-1"><X className="w-4 h-4" /></button>)}
                  </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setRenewContractId(null); setRenewalFile(null); setRenewalAdjustmentPercentage(''); }} className="flex-1 px-4 py-2 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt font-medium text-sm transition-colors">Cancelar</button>
                <button onClick={handleConfirmRenewal} className="flex-1 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 font-medium text-sm shadow-sm transition-colors">Confirmar Renovação</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Acompanhamento de Parcelas */}
      {billingTrackingContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className={`p-6 border-b ${
              billingTrackingContract.contractType === 'SOLAR'
                ? 'border-brand-200 dark:border-[rgba(203,106,30,0.30)] bg-brand-50 dark:bg-[rgba(203,106,30,0.15)]'
                : 'border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50'
            }`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      billingTrackingContract.contractType === 'SOLAR'
                        ? 'bg-orange-100 dark:bg-orange-900/40'
                        : 'bg-blue-100 dark:bg-blue-900/40'
                    }`}>
                      {billingTrackingContract.contractType === 'SOLAR' ? (
                        <svg className="w-6 h-6 text-orange-600 dark:text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 4 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${
                        billingTrackingContract.contractType === 'SOLAR'
                          ? 'text-orange-800 dark:text-orange-200'
                          : 'text-blue-900'
                      }`}>
                        Acompanhamento de Parcelas
                      </h3>
                      <p className={`text-sm ${
                        billingTrackingContract.contractType === 'SOLAR'
                          ? 'text-orange-700 dark:text-orange-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}>
                        {billingTrackingContract.title}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setBillingTrackingContract(null)}
                  className={`p-2 rounded-lg transition-colors ${
                    billingTrackingContract.contractType === 'SOLAR'
                      ? 'text-orange-400 hover:text-orange-600 hover:bg-orange-100'
                      : 'text-blue-400 hover:text-blue-600 hover:bg-blue-100'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">

              {/* Resumo do Contrato */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`rounded-lg p-4 border ${
                  billingTrackingContract.contractType === 'SOLAR'
                    ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800/60'
                    : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/60'
                }`}>
                  <p className={`text-xs font-semibold uppercase mb-1 ${
                    billingTrackingContract.contractType === 'SOLAR'
                      ? 'text-orange-600 dark:text-orange-300'
                      : 'text-blue-600 dark:text-blue-300'
                  }`}>
                    Total de Parcelas
                  </p>
                  <p className={`text-2xl font-bold ${
                    billingTrackingContract.contractType === 'SOLAR'
                      ? 'text-orange-800 dark:text-orange-200'
                      : 'text-blue-900'
                  }`}>
                    {billingTrackingContract.totalMonths || 0}
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 border border-green-200 dark:border-green-800/60">
                  <p className="text-xs text-green-600 dark:text-green-300 font-semibold uppercase mb-1">Parcelas Faturadas</p>
                  <p className="text-2xl font-bold text-green-900">{billingTrackingContract.billedInstallments || 0}</p>
                </div>

                <div className={`rounded-lg p-4 border ${
                  billingTrackingContract.contractType === 'SOLAR'
                    ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200'
                    : 'bg-indigo-50 border-indigo-200'
                }`}>
                  <p className={`text-xs font-semibold uppercase mb-1 ${
                    billingTrackingContract.contractType === 'SOLAR'
                      ? 'text-yellow-600'
                      : 'text-indigo-600 dark:text-indigo-300'
                  }`}>
                    Parcelas Restantes
                  </p>
                  <p className={`text-2xl font-bold ${
                    billingTrackingContract.contractType === 'SOLAR'
                      ? 'text-yellow-900'
                      : 'text-indigo-900'
                  }`}>
                    {(billingTrackingContract.totalMonths || 0) - (billingTrackingContract.billedInstallments || 0)}
                  </p>
                </div>
              </div>

              {/* Barra de Progresso Grande */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-ink-text dark:text-ink-text-dark">Progresso de Faturamento</span>
                  <span className={`text-sm font-bold ${
                    billingTrackingContract.contractType === 'SOLAR'
                      ? 'text-orange-600 dark:text-orange-300'
                      : 'text-blue-600 dark:text-blue-300'
                  }`}>
                    {billingTrackingContract.totalMonths
                      ? Math.round(((billingTrackingContract.billedInstallments || 0) / billingTrackingContract.totalMonths) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-surface-alt dark:bg-surface-dark-alt rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 flex items-center justify-end px-2 ${
                      billingTrackingContract.contractType === 'SOLAR'
                        ? 'bg-brand-500'
                        : 'bg-gradient-to-r from-blue-400 to-blue-600'
                    }`}
                    style={{
                      width: `${billingTrackingContract.totalMonths
                        ? ((billingTrackingContract.billedInstallments || 0) / billingTrackingContract.totalMonths) * 100
                        : 0}%`
                    }}
                  >
                    {((billingTrackingContract.billedInstallments || 0) / (billingTrackingContract.totalMonths || 1)) > 0.1 && (
                      <span className="text-[10px] font-bold text-white">
                        {billingTrackingContract.billedInstallments}/{billingTrackingContract.totalMonths}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Informações Adicionais */}
              <div className="bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-4 mb-6 border border-line dark:border-line-dark">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark font-semibold uppercase mb-1">Valor Total do Contrato</p>
                    <p className="text-lg font-bold text-ink-title dark:text-ink-title-dark">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(billingTrackingContract.value)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark font-semibold uppercase mb-1">Valor por Parcela (Estimado)</p>
                    <p className="text-lg font-bold text-ink-title dark:text-ink-title-dark">
                      {billingTrackingContract.totalMonths
                        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                            toFiniteNumber(billingTrackingContract.value) / billingTrackingContract.totalMonths
                          )
                        : 'N/A'}
                    </p>
                  </div>

                  {billingTrackingContract.firstBillingDate && (
                    <div>
                      <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark font-semibold uppercase mb-1">Data do 1º Faturamento</p>
                      <p className="text-sm font-medium text-ink-text dark:text-ink-text-dark">
                        {formatCalendarDate(billingTrackingContract.firstBillingDate)}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark font-semibold uppercase mb-1">Vigência</p>
                    <p className="text-sm font-medium text-ink-text dark:text-ink-text-dark">
                      {formatCalendarDate(billingTrackingContract.startDate)} até{' '}
                      {formatCalendarDate(billingTrackingContract.endDate)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Atualizar Faturamento */}
              <div className="bg-orange-50 dark:bg-orange-900/30 border-2 border-orange-200 dark:border-orange-800/60 rounded-xl p-6">
                <h4 className="text-lg font-bold text-orange-800 dark:text-orange-200 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Atualizar Parcelas Faturadas
                </h4>

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-orange-800 dark:text-orange-200 mb-2">
                      Quantas parcelas foram faturadas até agora?
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={billingTrackingContract.totalMonths || 0}
                      value={billingTrackingContract.billedInstallments || 0}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value) || 0;
                        setBillingTrackingContract({
                          ...billingTrackingContract,
                          billedInstallments: Math.min(newValue, billingTrackingContract.totalMonths || 0)
                        });
                      }}
                      className="w-full border-2 border-orange-300 rounded-lg px-4 py-3 text-lg font-bold text-orange-800 dark:text-orange-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-surface dark:bg-surface-dark"
                    />
                    <p className="text-xs text-orange-600 dark:text-orange-300 mt-2">
                      💡 Atualize este número sempre que uma nova parcela for faturada
                    </p>
                  </div>

                  {/* Botões de Ação Rápida */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        const current = billingTrackingContract.billedInstallments || 0;
                        if (current > 0) {
                          setBillingTrackingContract({
                            ...billingTrackingContract,
                            billedInstallments: current - 1
                          });
                        }
                      }}
                      className="px-4 py-2 bg-surface dark:bg-surface-dark border-2 border-orange-300 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-50 font-medium text-sm transition-colors"
                      disabled={isUpdatingBilling || (billingTrackingContract.billedInstallments || 0) === 0}
                    >
                      ➖ Diminuir 1
                    </button>

                    <button
                      onClick={() => {
                        const current = billingTrackingContract.billedInstallments || 0;
                        const max = billingTrackingContract.totalMonths || 0;
                        if (current < max) {
                          setBillingTrackingContract({
                            ...billingTrackingContract,
                            billedInstallments: current + 1
                          });
                        }
                      }}
                      className="px-4 py-2 bg-surface dark:bg-surface-dark border-2 border-orange-300 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-50 font-medium text-sm transition-colors"
                      disabled={isUpdatingBilling || (billingTrackingContract.billedInstallments || 0) >= (billingTrackingContract.totalMonths || 0)}
                    >
                      ➕ Adicionar 1
                    </button>

                    <button
                      onClick={() => {
                        setBillingTrackingContract({
                          ...billingTrackingContract,
                          billedInstallments: billingTrackingContract.totalMonths || 0
                        });
                      }}
                      className="px-4 py-2 bg-green-100 dark:bg-green-900/40 border-2 border-green-300 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 font-medium text-sm transition-colors"
                      disabled={isUpdatingBilling}
                    >
                      ✅ Marcar Todas Faturadas
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt flex justify-between gap-3">
              <button
                onClick={() => setBillingTrackingContract(null)}
                className="px-4 py-2 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt font-medium text-sm transition-colors"
              >
                Cancelar
              </button>

              <button
                onClick={() => handleUpdateBilling(billingTrackingContract.id, billingTrackingContract.billedInstallments || 0)}
                disabled={isUpdatingBilling}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  isUpdatingBilling
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700 shadow-sm'
                }`}
              >
                {isUpdatingBilling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar Atualização
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Contrato */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                <h3 className="text-lg font-bold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                    <Plus className="w-5 h-5 text-blue-600 dark:text-blue-300" /> Novo Contrato
                </h3>
                <button onClick={() => setIsCreating(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark p-1 rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
                {/* Área de Upload Inteligente */}
                <div className="mb-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg text-blue-600 dark:text-blue-300">
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-bold text-blue-900 mb-1">Preenchimento Automático com IA</h4>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">Faça upload do PDF do contrato e a IA preencherá os campos automaticamente para você.</p>

                            <label className={`flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer transition-colors ${isExtracting ? 'bg-blue-100 dark:bg-blue-900/40 opacity-70 cursor-wait' : 'bg-surface dark:bg-surface-dark hover:bg-blue-50 hover:border-blue-400'}`}>
                                <input type="file" className="hidden" accept=".pdf" onChange={handleFileUpload} disabled={isExtracting} />
                                {isExtracting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-300" />
                                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Analisando documento...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Clique para carregar PDF do Contrato</span>
                                    </>
                                )}
                            </label>
                            <div className="mt-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                                <button onClick={() => setShowPromptHelp(p => !p)} className="underline text-blue-600 dark:text-blue-300">{showPromptHelp ? 'Ocultar Prompt IA' : 'Ver Prompt IA (regras para extração)'}</button>
                                {showPromptHelp && (
                                  <div className="mt-2 p-3 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded">
                                    <div className="flex items-start gap-2">
                                      <pre className="text-xs text-ink-text dark:text-ink-text-dark whitespace-pre-wrap">{detailedContractPrompt}</pre>
                                    </div>
                                    <div className="mt-2 flex gap-2">
                                      <button onClick={() => navigator.clipboard.writeText(detailedContractPrompt)} className="px-3 py-1 bg-brand-500 text-white rounded text-xs">Copiar Detalhado</button>
                                      <button onClick={() => navigator.clipboard.writeText(jsonContractPrompt)} className="px-3 py-1 bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark rounded text-xs">Copiar JSON</button>
                                      <button onClick={() => navigator.clipboard.writeText(shortContractPrompt)} className="px-3 py-1 bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark rounded text-xs">Copiar Curto</button>
                                    </div>
                                  </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Título do Contrato *</label>
            <input type="text" value={newContractForm.title || ''} onChange={e => setNewContractForm({...newContractForm, title: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Ex: Contrato de Fornecimento de Papel" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Número do Contrato</label>
            <input type="text" value={newContractForm.contractNumber || ''} onChange={e => setNewContractForm({...newContractForm, contractNumber: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Ex: 276/2025" />
          </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Órgão Contratante *</label>
                        <input type="text" value={newContractForm.organ || ''} onChange={e => setNewContractForm({...newContractForm, organ: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Ex: Prefeitura Municipal..." />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Empresa Vinculada *</label>
                        <select value={newContractForm.companyId || ''} onChange={e => setNewContractForm({...newContractForm, companyId: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark">
                            <option value="">Selecione uma empresa...</option>
                            {companies.map(c => <option key={c.id} value={c.id}>{c.corporateName}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Tipo de Contrato</label>
            <select value={newContractForm.type || 'OTHER'} onChange={e => setNewContractForm({...newContractForm, type: e.target.value as any})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark">
              <option value="PRICE_REGISTRATION">Ata de Registro de Preço</option>
              <option value="SUPPLY">Fornecimento</option>
              <option value="SERVICE">Serviço</option>
              <option value="MAINTENANCE">Manutenção</option>
              <option value="RENTAL">Locação</option>
              <option value="RENTAL_MAINTENANCE">Locação + Manutenção</option>
              <option value="OTHER">Outros</option>
            </select>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1 flex items-center gap-2">
                            Classificação do Contrato
                            <span className="text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full normal-case font-normal">Opcional - Sistema classifica automaticamente</span>
                        </label>
                        <select
                            value={newContractForm.contractType || 'STANDARD'}
                            onChange={e => setNewContractForm({...newContractForm, contractType: e.target.value as 'STANDARD' | 'SOLAR'})}
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                        >
                            <option value="STANDARD">📄 Contrato Padrão</option>
                            <option value="SOLAR">☀️ Contrato Solar (Locação + Comodato)</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Categoria *</label>
                        <select value={newContractForm.category || 'PUBLIC'} onChange={e => setNewContractForm({...newContractForm, category: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark">
                            <option value="PUBLIC">🏛️ Público</option>
                            <option value="PRIVATE">🔒 Privado</option>
                        </select>
                    </div>

          <div>
            <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Valor Global (R$)</label>
            <MoneyInput
              className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
              placeholder="0,00"
              value={typeof newContractForm.value === 'number' ? newContractForm.value : null}
              onValueChange={(v) => setNewContractForm({...newContractForm, value: v ?? undefined})}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Valor Mensal Vigente (R$)</label>
            <MoneyInput
              className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
              placeholder="0,00"
              value={typeof newContractForm.monthlyValue === 'number' ? newContractForm.monthlyValue : null}
              onValueChange={(v) => setNewContractForm({...newContractForm, monthlyValue: v ?? undefined})}
            />
          </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Início da Vigência *</label>
                        <input type="date" value={newContractForm.startDate ? new Date(newContractForm.startDate).toISOString().split('T')[0] : ''} onChange={e => setNewContractForm({...newContractForm, startDate: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Fim da Vigência *</label>
                        <input type="date" value={newContractForm.endDate ? new Date(newContractForm.endDate).toISOString().split('T')[0] : ''} onChange={e => setNewContractForm({...newContractForm, endDate: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" />
                    </div>

                    {/* Seção de Acompanhamento de Parcelas (Opcional) */}
                    <div className="col-span-2 mt-4">
                        <div className={`border rounded-lg p-4 ${newContractForm.contractType === 'SOLAR' ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800/60' : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/60'}`}>
                            <div className="flex items-center gap-2 mb-3">
                                {newContractForm.contractType === 'SOLAR' ? (
                                    <svg className="w-5 h-5 text-orange-600 dark:text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                )}
                                <h4 className={`text-sm font-bold ${newContractForm.contractType === 'SOLAR' ? 'text-orange-800 dark:text-orange-200' : 'text-blue-900'}`}>
                                    {newContractForm.contractType === 'SOLAR' ? 'Acompanhamento de Parcelas Solares' : 'Acompanhamento de Parcelas (Opcional)'}
                                </h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={`block text-xs font-semibold uppercase mb-1 ${newContractForm.contractType === 'SOLAR' ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'}`}>
                                        Total de Meses (Parcelas)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="240"
                                        value={newContractForm.totalMonths || ''}
                                        onChange={e => setNewContractForm({...newContractForm, totalMonths: e.target.value ? parseInt(e.target.value) : undefined})}
                                        className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 outline-none bg-surface dark:bg-surface-dark ${
                                            newContractForm.contractType === 'SOLAR'
                                                ? 'border-orange-300 focus:ring-orange-500'
                                                : 'border-blue-300 focus:ring-brand-500'
                                        }`}
                                        placeholder={newContractForm.contractType === 'SOLAR' ? '48' : 'Ex: 12, 24, 36...'}
                                    />
                                    <p className={`text-[10px] mt-1 ${newContractForm.contractType === 'SOLAR' ? 'text-orange-600 dark:text-orange-300' : 'text-blue-600 dark:text-blue-300'}`}>
                                        {newContractForm.contractType === 'SOLAR'
                                            ? 'Típico: 48 meses (4 anos)'
                                            : 'Deixe vazio se não parcelado'}
                                    </p>
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold uppercase mb-1 ${newContractForm.contractType === 'SOLAR' ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'}`}>
                                        Parcelas Já Faturadas
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newContractForm.billedInstallments || 0}
                                        onChange={e => setNewContractForm({...newContractForm, billedInstallments: parseInt(e.target.value)})}
                                        className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 outline-none bg-surface dark:bg-surface-dark ${
                                            newContractForm.contractType === 'SOLAR'
                                                ? 'border-orange-300 focus:ring-orange-500'
                                                : 'border-blue-300 focus:ring-brand-500'
                                        }`}
                                        placeholder="0"
                                        disabled={!newContractForm.totalMonths}
                                    />
                                    <p className={`text-[10px] mt-1 ${newContractForm.contractType === 'SOLAR' ? 'text-orange-600 dark:text-orange-300' : 'text-blue-600 dark:text-blue-300'}`}>
                                        Deixe 0 se ainda não foi faturado
                                    </p>
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold uppercase mb-1 ${newContractForm.contractType === 'SOLAR' ? 'text-orange-700 dark:text-orange-300' : 'text-blue-700 dark:text-blue-300'}`}>
                                        Data do 1º Faturamento
                                    </label>
                                    <input
                                        type="date"
                                        value={newContractForm.firstBillingDate ? new Date(newContractForm.firstBillingDate).toISOString().split('T')[0] : ''}
                                        onChange={e => setNewContractForm({...newContractForm, firstBillingDate: e.target.value})}
                                        className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 outline-none ${
                                            newContractForm.contractType === 'SOLAR'
                                                ? 'border-orange-300 focus:ring-orange-500'
                                                : 'border-blue-300 focus:ring-brand-500'
                                        }`}
                                        disabled={!newContractForm.totalMonths}
                                    />
                                    <p className={`text-[10px] mt-1 ${newContractForm.contractType === 'SOLAR' ? 'text-orange-600 dark:text-orange-300' : 'text-blue-600 dark:text-blue-300'}`}>
                                        Quando começou/começará
                                    </p>
                                </div>
                            </div>

                            <div className={`mt-3 p-3 bg-surface dark:bg-surface-dark rounded border ${newContractForm.contractType === 'SOLAR' ? 'border-orange-200 dark:border-orange-800/60' : 'border-blue-200 dark:border-blue-800/60'}`}>
                                <p className={`text-xs ${newContractForm.contractType === 'SOLAR' ? 'text-orange-800 dark:text-orange-200' : 'text-blue-800 dark:text-blue-200'}`}>
                                    {newContractForm.contractType === 'SOLAR' ? (
                                        <><strong>ℹ️ Sobre Contratos Solares:</strong> Contratos híbridos com locação de equipamentos + comodato de instalação.
                                        Geralmente possuem 48 parcelas mensais. O sistema acompanhará automaticamente o faturamento e alertará sobre parcelas pendentes.</>
                                    ) : (
                                        <><strong>ℹ️ Acompanhamento de Parcelas:</strong> Se o contrato tiver pagamento parcelado, preencha os campos acima.
                                        O sistema permitirá acompanhar quantas parcelas foram faturadas. <strong>Contratos vigentes sem nenhuma parcela paga aparecerão na aba "Vigentes sem Faturamento".</strong></>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Mantido para compatibilidade mas agora oculto */}
                    {false && newContractForm.contractType === 'SOLAR' && (
                        <>
                            <div className="col-span-2 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800/60 rounded-lg p-4 mt-2">
                                <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-5 h-5 text-orange-600 dark:text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                                    </svg>
                                    <h4 className="text-sm font-bold text-orange-800 dark:text-orange-200">Configurações de Contrato Solar</h4>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase mb-1">Total de Meses (Parcelas)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="120"
                                            value={newContractForm.totalMonths || 48}
                                            onChange={e => setNewContractForm({...newContractForm, totalMonths: parseInt(e.target.value)})}
                                            className="w-full border border-orange-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none bg-surface dark:bg-surface-dark"
                                            placeholder="48"
                                        />
                                        <p className="text-[10px] text-orange-600 dark:text-orange-300 mt-1">Típico: 48 meses (4 anos)</p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase mb-1">Parcelas Já Faturadas</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={newContractForm.billedInstallments || 0}
                                            onChange={e => setNewContractForm({...newContractForm, billedInstallments: parseInt(e.target.value)})}
                                            className="w-full border border-orange-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none bg-surface dark:bg-surface-dark"
                                            placeholder="0"
                                        />
                                        <p className="text-[10px] text-orange-600 dark:text-orange-300 mt-1">Deixe 0 se ainda não foi faturado</p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase mb-1">Data do 1º Faturamento</label>
                                        <input
                                            type="date"
                                            value={newContractForm.firstBillingDate ? new Date(newContractForm.firstBillingDate).toISOString().split('T')[0] : ''}
                                            onChange={e => setNewContractForm({...newContractForm, firstBillingDate: e.target.value})}
                                            className="w-full border border-orange-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                        />
                                        <p className="text-[10px] text-orange-600 dark:text-orange-300 mt-1">Quando começou/começará a cobrança</p>
                                    </div>
                                </div>

                                <div className="mt-3 p-3 bg-surface dark:bg-surface-dark rounded border border-orange-200 dark:border-orange-800/60">
                                    <p className="text-xs text-orange-800 dark:text-orange-200">
                                        <strong>ℹ️ Sobre Contratos Solares:</strong> Contratos híbridos com locação de equipamentos + comodato de instalação.
                                        Geralmente possuem 48 parcelas mensais. O sistema acompanhará automaticamente o faturamento e alertará sobre parcelas pendentes.
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase mb-1">Descrição / Objeto</label>
                        <textarea value={newContractForm.description || ''} onChange={e => setNewContractForm({...newContractForm, description: e.target.value})} className="w-full border border-line-strong dark:border-line-dark-strong rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none h-24" placeholder="Descrição detalhada do objeto do contrato..." />
                    </div>
                </div>
            </div>

      {/* Seção de Anexos Complementares (Novo Contrato) */}
      <div className="p-6 border-t border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
        <h4 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-2 flex items-center gap-2"><Paperclip className="w-4 h-4" /> Anexos Complementares (opcionais)</h4>
        <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-3">Adicione arquivos complementares (ART, Atestado, Planilhas). Eles serão enviados ao S3 e vinculados ao contrato quando criado.</p>
        <div className="flex items-center gap-3">
          <label className="flex-1 cursor-pointer bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong border-dashed rounded-lg p-2 text-center hover:bg-blue-50 transition-colors">
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple onChange={handleAddAttachments} />
            <div className="flex items-center justify-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
              <Upload className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /> Selecionar arquivos
            </div>
          </label>
          {newAttachments.length > 0 && (
            <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{newAttachments.length} arquivo{newAttachments.length > 1 ? 's' : ''} selecionado{newAttachments.length > 1 ? 's' : ''}</div>
          )}
        </div>

        {/* Lista de Anexos Pendentes */}
        {newAttachments.length > 0 && (
          <div className="mt-3 bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark rounded p-3 text-sm">
            {newAttachments.map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1">
                <div className="truncate">{f.name} <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">({(f.size/1024).toFixed(2)} KB)</span></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleRemoveNewAttachment(i)} className="text-red-500 dark:text-red-400 hover:bg-red-50 rounded p-1 text-xs">Remover</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt flex justify-end gap-3">
                <button onClick={() => setIsCreating(false)} className="px-4 py-2 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt font-medium text-sm transition-colors">Cancelar</button>
        <button onClick={handleCreateContract} className={`${theme.classes.button.primary} px-6 py-2 flex items-center gap-2 text-sm`}>
          <Save className="w-4 h-4" /> Criar Contrato
        </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação - Contrato Existente */}
      {showEditConfirmModal && existingContractToEdit && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Contrato Já Existe</h3>
                  <p className="text-sm opacity-90">Número de contrato duplicado</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-900 font-medium mb-2">
                  ⚠️ Já existe um contrato com o número <strong className="text-amber-700">"{existingContractToEdit.contractNumber}"</strong>
                </p>
                <p className="text-xs text-amber-700">
                  Deseja editar o contrato existente ou utilizar outro número?
                </p>
              </div>

              <div className="bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-4 space-y-2 mb-4">
                <h4 className="text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase">Contrato Existente:</h4>
                <div className="space-y-1 text-sm">
                  <p><strong className="text-ink-text dark:text-ink-text-dark">Título:</strong> {existingContractToEdit.title}</p>
                  <p><strong className="text-ink-text dark:text-ink-text-dark">Órgão:</strong> {existingContractToEdit.organ}</p>
                  <p><strong className="text-ink-text dark:text-ink-text-dark">Tipo:</strong> {existingContractToEdit.type}</p>
                  <p><strong className="text-ink-text dark:text-ink-text-dark">Valor:</strong> {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(existingContractToEdit.value || 0)}</p>
                  {existingContractToEdit.startDate && (
                    <p><strong className="text-ink-text dark:text-ink-text-dark">Vigência:</strong> {formatCalendarDate(existingContractToEdit.startDate)} - {formatCalendarDate(existingContractToEdit.endDate, 'Indeterminado')}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleEditExistingContract}
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <Edit className="w-4 h-4" />
                  Editar Contrato Existente
                </button>

                <button
                  onClick={handleCancelEditExisting}
                  className="w-full bg-surface dark:bg-surface-dark hover:bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark font-medium py-3 px-4 rounded-lg border border-line-strong dark:border-line-dark-strong transition-colors"
                >
                  Voltar e Alterar Número
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização/Gestão de Anexos Complementares (acessível dos cards) */}
      {attachmentsModalOpen && selectedContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
              <h3 className="text-lg font-bold text-ink-title dark:text-ink-title-dark flex items-center gap-2"><Eye className="w-5 h-5 text-blue-600 dark:text-blue-300" /> Anexos Complementares</h3>
              <button onClick={() => setAttachmentsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark p-1 rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              {/* Upload controls inside the modal so users can add attachments directly */}
              <div className="flex items-center gap-3 mb-4">
                <label className="flex-1 cursor-pointer bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong border-dashed rounded-lg p-2 text-center hover:bg-blue-50 transition-colors">
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple onChange={handleAddAttachments} />
                  <div className="flex items-center justify-center gap-2 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                    <Upload className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /> Selecionar arquivos
                  </div>
                </label>
                <button onClick={handleUploadAttachments} disabled={attachmentsUploading || newAttachments.length === 0} className={`px-3 py-2 rounded text-sm ${attachmentsUploading || newAttachments.length === 0 ? 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark' : 'bg-brand-500 text-white hover:bg-brand-600'}`}>
                  {attachmentsUploading ? 'Enviando...' : 'Enviar Anexos'}
                </button>
              </div>

              {/* Lista de anexos pendentes (selecionados para envio) */}
              {newAttachments.length > 0 && (
                <div className="mb-3 text-sm">
                  <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">Arquivos selecionados (prontos para enviar):</div>
                  <div className="space-y-2">
                    {newAttachments.map((f, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <div className="truncate"><div className="text-sm">{f.name}</div><div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{f.type} • {(f.size/1024).toFixed(2)} KB</div></div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleRemoveNewAttachment(i)} className="text-xs text-red-600 dark:text-red-300">Remover</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista de anexos já salvos */}
              {((editForm && editForm.attachments) || selectedContract?.attachments || []).length > 0 ? (
                <div className="space-y-3">
                  {((editForm && editForm.attachments) || selectedContract?.attachments || []).map(att => (
                    <div key={att.id} className="flex items-center justify-between gap-3">
                      <div className="truncate">
                        <div className="text-sm font-medium">{att.name}</div>
                        <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">{att.type} {att.size ? `• ${(att.size/1024).toFixed(2)} KB` : ''}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleDownloadAttachment(att)} className="px-2 py-1 bg-brand-500 text-white rounded text-xs">Visualizar</button>
                        <button onClick={async () => { await handleDeleteAttachment(att.id); }} className="px-2 py-1 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 rounded text-xs border border-red-100 dark:border-red-800/50">Remover</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhum anexo complementar encontrado para este contrato.</div>
              )}
            </div>
            <div className="p-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt flex justify-end gap-3">
              <button onClick={() => setAttachmentsModalOpen(false)} className="px-4 py-2 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt font-medium text-sm transition-colors">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContractList;
